import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  Accordion,
  Form,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap'
import type { OverlayInjectedProps } from 'react-bootstrap/Overlay'
import classNames from 'classnames'
import ChartingSelectToothFirstModal from '@components/pages/Charting/Modals/ChartingSelectToothFirstModal.component'
import ChartingSelectConditionFirstModal from '@components/pages/Charting/Modals/ChartingSelectConditionFirstModal.component'
import {
  ApplyConditionPayload,
  IDentalData,
} from '@dataTypes/chartingFeature.types'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { getFullName } from '@utils/fullname.util'
import {
  CHART_PROVIDER_INVALID_ERROR,
  isChartProviderHardError,
} from '@utils/chart-validation.utils'
import {
  buildProviderSearchList,
  resolveChartProviderDisplayName,
} from '@utils/provider-options.util'
import { ComboBoxComponent } from '@syncfusion/ej2-react-dropdowns'
import ChartNameComboBox from '@components/pages/Charting/ChartNameComboBox.component'
import {
  PREMOLAR_TOOTH_IDS,
  LOWER_PREMOLAR_TOOTH_IDS,
  INCISOR_CANINE_TOOTH_IDS,
  LOWER_INCISOR_CANINE_TOOTH_IDS,
  MOLAR_TOOTH_IDS,
  LOWER_MOLAR_TOOTH_IDS,
} from '@constants/Charting/conditions.schema'
import {
  setCreateDentalChart,
  setDentalUpdated,
} from '@store/reducers/charting.reducer'
import { useActiveDentalChart } from '@hooks/useActiveDentalChart'
import {
  getDentalDataForTooth,
  addConditionToDentalData,
  removeConditionFromDentalData,
} from '@utils/dental-data.utils'
import {
  EXCLUSIVE_CONDITIONS,
  CONDITIONS,
  ENDODONTICS,
  ORTHODONTICS,
  SURFACE_LABEL_MAP,
  getSurfaceToggleDiff,
  REGION_DEPENDENT_CONDITIONS,
  FURCATION_ALLOWED_TOOTH_IDS,
  VENEER_RESTRICTED_TOOTH_IDS,
  INLAY_ONLAY_RESTRICTED_TOOTH_IDS,
  SEALANTS_RESTRICTED_TOOTH_IDS,
  RETAINER_ALLOWED_TOOTH_IDS,
  FURCATION_TYPE_CODES,
  PANEL_AUTO_EXPAND_CONDITION_CODES,
  ORTHODONTIC_CODES_WITH_SUB_ITEMS,
  CONDITION_CODES_WITH_SURFACE_GRID,
  CONDITION_CODES_WITH_SUB_ITEMS,
} from '../utils/charting-side-panel.constants'
import {
  isConditionApplicableToTooth,
  getConditionFromLabel,
  capitalizeFirstLetter,
  conditionHasSubItems,
  getAvailableFurcationAreas,
  getSelectedFurcationAreas,
} from '../utils/condition.utils'
import {
  getFractureAcronym,
  getFractureFullName,
} from '../utils/fracture.utils'
import { getChartMaterialColor } from '../utils/material.utils'
import {
  getSurfaceRows,
  getAbrasionSurfaceRows,
  wearSurfaceFromGridCode,
  storedWearSurfaceMatches,
} from '../utils/surface.utils'
import type { FillingMaterial } from '../tooth-svgs/types/tooth.types'
import { formatSurfaceFullName } from '@utils/tooth-tooltip.utils'
import Button from '@components/common/Button/Button.component'

const chartingSidepanelToggleClass = (
  isChecked: boolean,
  modifier?: 'half' | 'crack',
): string =>
  classNames('button-secondary', 'charting-sidepanel-button', {
    active: isChecked,
    'charting-sidepanel-button--half': modifier === 'half',
    'charting-sidepanel-button--crack': modifier === 'crack',
  })

/** Avoid Popper flash / scroll shake inside overflow panels (see Reset tooltip pattern). */
const CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS = {
  transition: false as const,
  popperConfig: { strategy: 'fixed' as const },
}

const SURFACE_LABEL_ABBREVIATIONS: Record<string, string> = {
  Buccal: 'B',
  Facial: 'F',
  Mesial: 'M',
  Distal: 'D',
  Occlusal: 'O',
  Incisal: 'I',
  Palatal: 'P',
  Lingual: 'L',
  'Buccal Cusp': 'BC',
  'Facial Cusp': 'FC',
  'Palatal Cusp': 'PC',
  'Lingual Cusp': 'LC',
  MIFL: 'MIFL',
  DIFL: 'DIFL',
  MIFP: 'MIFP',
  DIFP: 'DIFP',
  MOB: 'MOB',
  MOP: 'MOP',
  DOB: 'DOB',
  DOP: 'DOP',
  MOL: 'MOL',
  DOL: 'DOL',
  Mesiobuccal: 'MB',
  Distobuccal: 'DB',
  Mesiofacial: 'MF',
  Distofacial: 'DF',
  'Cervical Buccal': 'CB',
  'Cervical Facial': 'CF',
  'Cervical Palatal': 'CP',
  'Cervical Lingual': 'CL',
  'Mesio Buccal Cusp': 'MOB',
  'Disto Buccal Cusp': 'DOB',
  'Mesio Facial Cusp': 'MOB',
  'Disto Facial Cusp': 'DOB',
  'Mesio Palatal Cusp': 'MOP',
  'Disto Palatal Cusp': 'DOP',
  'Mesio Lingual Cusp': 'MOL',
  'Disto Lingual Cusp': 'DOL',
  'Facial Surface': 'FS',
  'Palatal Surface': 'PS',
  'Lingual Surface': 'LS',
}

type ChartingSidePanelTooltipOverlayFn = (
  injected: OverlayInjectedProps,
) => React.ReactElement

const createChartingSidePanelTooltipOverlay = (
  id: string,
  tooltipClassName: string,
  children: React.ReactNode,
): ChartingSidePanelTooltipOverlayFn => {
  const renderOverlay = (
    injected: OverlayInjectedProps,
  ): React.ReactElement => (
    <Tooltip
      {...injected}
      id={id}
      className={classNames(tooltipClassName, injected.className)}
      style={{
        ...injected.style,
        ...(injected.show && !injected.hasDoneInitialMeasure
          ? { visibility: 'hidden', opacity: 0, pointerEvents: 'none' }
          : {}),
      }}
    >
      {children}
    </Tooltip>
  )
  renderOverlay.displayName = `ChartingSidePanelTooltipOverlay`
  return renderOverlay
}

const getSurfaceLabelAbbreviation = (label: string): string => {
  return SURFACE_LABEL_ABBREVIATIONS[label] ?? label
}

/** MOB/MIFP/… compound implication for molars, premolars, and anteriors. */
const shouldAutoImplyCuspsForTooth = (toothNumber: number | null): boolean => {
  if (toothNumber === null) {
    return false
  }
  return (
    MOLAR_TOOTH_IDS.includes(toothNumber) ||
    LOWER_MOLAR_TOOTH_IDS.includes(toothNumber) ||
    PREMOLAR_TOOTH_IDS.includes(toothNumber) ||
    LOWER_PREMOLAR_TOOTH_IDS.includes(toothNumber) ||
    INCISOR_CANINE_TOOTH_IDS.includes(toothNumber) ||
    LOWER_INCISOR_CANINE_TOOTH_IDS.includes(toothNumber)
  )
}

const FRACTURE_SURFACE_LABEL_CODE_MAP: Record<string, string> = {
  MIFP: 'mifp',
  DIFP: 'difp',
  MIFL: 'mifl',
  DIFL: 'difl',
  MOB: 'mob',
  MOP: 'mop',
  DOB: 'dob',
  DOP: 'dop',
  MOL: 'mol',
  DOL: 'dol',
}

const getSurfaceTooltipTitle = (label: string): string => {
  const fractureCode = FRACTURE_SURFACE_LABEL_CODE_MAP[label]
  const surfaceCode = fractureCode ?? SURFACE_LABEL_MAP[label]
  if (!surfaceCode) {
    return label
  }
  return formatSurfaceFullName(surfaceCode.toLowerCase())
}

// Helper function to render fracture tooltip with white color
const renderFractureTooltip = (
  fractureName: string,
): ChartingSidePanelTooltipOverlayFn => {
  return createChartingSidePanelTooltipOverlay(
    `fracture-tooltip-${fractureName}`,
    'fracture-tooltip',
    <div style={{ margin: 0 }}>
      <span style={{ whiteSpace: 'nowrap', lineHeight: 1 }}>
        {fractureName}
      </span>
    </div>,
  )
}

// Helper function to render material tooltip
const renderMaterialTooltip = (
  materialName: string,
  chartColor: string,
): ChartingSidePanelTooltipOverlayFn => {
  return createChartingSidePanelTooltipOverlay(
    `material-tooltip-${materialName}`,
    'material-color-tooltip',
    <div
      className="d-flex align-items-center"
      style={{ margin: 0, gap: '4px' }}
    >
      <span style={{ whiteSpace: 'nowrap', lineHeight: 1 }}>
        {materialName}
      </span>
      <span
        className="material-color-swatch"
        style={{
          backgroundColor: chartColor,
          width: '16px',
          height: '16px',
          borderRadius: '3px',
          border: '1px solid #ddd',
          display: 'inline-block',
          flexShrink: 0,
        }}
      />
    </div>,
  )
}

const getFractureTypesForTooth = (selectedTooth: number | null): string[] => {
  if (!selectedTooth) {
    return ['mesiobuccal', 'distobuccal', 'mesiopalatal', 'distopalatal']
  }

  const defaultUpperMolarTypes = [
    'mesiobuccal',
    'distobuccal',
    'mesiopalatal',
    'distopalatal',
  ]
  const defaultLowerMolarTypes = [
    'mesiobuccal',
    'distobuccal',
    'mesiolingual',
    'distolingual',
  ]

  const isPremolar =
    PREMOLAR_TOOTH_IDS.includes(selectedTooth) ||
    LOWER_PREMOLAR_TOOTH_IDS.includes(selectedTooth)
  if (isPremolar) {
    return selectedTooth >= 17 && selectedTooth <= 32
      ? defaultLowerMolarTypes
      : defaultUpperMolarTypes
  }

  const isAnterior =
    INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth) ||
    LOWER_INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth)
  if (isAnterior) {
    return selectedTooth >= 17 && selectedTooth <= 32
      ? [...defaultLowerMolarTypes, 'incisal', 'mifl', 'difl']
      : [...defaultUpperMolarTypes, 'incisal', 'mifp', 'difp']
  }

  return selectedTooth >= 17 && selectedTooth <= 32
    ? defaultLowerMolarTypes
    : defaultUpperMolarTypes
}

/** Remove every dental_data row for the given base condition on one tooth (bridge: clears neighbor abutments first). */
const removeAllConditionRowsForTooth = (
  dentalData: IDentalData[],
  toothNumber: number,
  conditionCode: string,
): IDentalData[] => {
  const toothStr = toothNumber.toString()
  return dentalData.filter(
    (data) =>
      !(
        String(data.tooth_number) === toothStr &&
        data.condition === conditionCode
      ),
  )
}

export type ChartingSidePanelLayout = 'accordion' | 'xray-split'

export interface ChartingSidePanelProps {
  selectedTooth: number | null
  activeChartId: string
  onApplyCondition: (
    payload: ApplyConditionPayload | ApplyConditionPayload[],
  ) => void
  validationErrors: { [key: string]: string }
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{ [key: string]: string }>
  >
  sidePanelLayout?: ChartingSidePanelLayout
  voiceStatus?: React.ReactNode
  /** When true, keep Voice assistant accordion open (mic connecting/listening/processing). */
  voiceActive?: boolean
  /** Bumped when manual save fails on Charting information fields. */
  chartingInfoFocusTick?: number
}

const VOICE_ASSISTANT_ACCORDION_KEY = '4'
const CHARTING_INFO_ACCORDION_KEY = '0'

const ChartingSidePanel = ({
  selectedTooth,
  activeChartId,
  onApplyCondition: onApplyConditionPayload,
  validationErrors,
  setValidationErrors,
  sidePanelLayout = 'accordion',
  voiceStatus,
  voiceActive = false,
  chartingInfoFocusTick = 0,
}: ChartingSidePanelProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const [activeAccordionKey, setActiveAccordionKey] = useState<string>('0')
  const [preferChartingInfo, setPreferChartingInfo] = useState(false)
  const [bridgeMaterialStage, setBridgeMaterialStage] = useState<
    'zirconia' | 'pfm' | 'pfz' | 'metal' | 'ceramic' | null
  >(null)

  /** Until filling is stored in dental_data, material chips use this draft default (first surface persists it). */
  const [fillingMaterialDraft, setFillingMaterialDraft] =
    useState<FillingMaterial>('ceramic')

  const [showConditionWarning, setShowConditionWarning] = useState(false)
  const [showNoToothModal, setShowNoToothModal] = useState(false)

  const [provId, setProvId] = useState('')
  const [chartName, setChartName] = useState('')

  const providers = useAppSelector((state) => state.provider.data)
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const providerSearchList = useMemo(
    () => buildProviderSearchList(providers, selectedClinicId),
    [providers, selectedClinicId],
  )

  useEffect((): (() => void) => {
    return (): void => {
      document
        .querySelectorAll('.tooltip, .popover')
        .forEach((node) => node.remove())
    }
  }, [])

  useEffect(() => {
    setFillingMaterialDraft('ceramic')
  }, [selectedTooth])
  const createDentalChart = useAppSelector(
    (state) => state.charting.createDentalChart,
  )
  const selectedDentalChart = useAppSelector(
    (state) => state.charting.selectedDentalChart,
  )
  const {
    activeDentalChart,
    isExplicitHistorySelection,
    isPersistedChart,
    commitActiveDentalChartUpdate,
  } = useActiveDentalChart()

  const toPayload = ({
    condition,
    type = null,
    material = null,
    surface = null,
  }: Partial<ApplyConditionPayload> &
    Pick<ApplyConditionPayload, 'condition'>): ApplyConditionPayload => ({
    condition,
    type,
    material,
    surface,
  })

  const getFractureTypesForSurface = useCallback(
    (surfaceCode: string): string[] => {
      const normalized = surfaceCode.toLowerCase()
      const isLowerTooth = selectedTooth !== null && selectedTooth >= 17
      const isAnteriorTooth =
        selectedTooth !== null &&
        (INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth) ||
          LOWER_INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth))

      switch (normalized) {
        case 'mob':
        case 'mb':
          return isAnteriorTooth
            ? ['mesiobuccal', isLowerTooth ? 'mesiolingual' : 'mesiopalatal']
            : ['mesiobuccal']
        case 'dob':
        case 'db':
          return isAnteriorTooth
            ? ['distobuccal', isLowerTooth ? 'distolingual' : 'distopalatal']
            : ['distobuccal']
        case 'mop':
        case 'mp':
          return [isLowerTooth ? 'mesiolingual' : 'mesiopalatal']
        case 'dop':
        case 'dp':
          return [isLowerTooth ? 'distolingual' : 'distopalatal']
        case 'mol':
        case 'ml':
          return ['mesiolingual']
        case 'dol':
        case 'dl':
          return ['distolingual']
        case 'i':
          return isAnteriorTooth ? ['incisal'] : []
        case 'mifp':
          return isAnteriorTooth && !isLowerTooth ? ['mifp'] : []
        case 'difp':
          return isAnteriorTooth && !isLowerTooth ? ['difp'] : []
        case 'mifl':
          return isAnteriorTooth && isLowerTooth ? ['mifl'] : []
        case 'difl':
          return isAnteriorTooth && isLowerTooth ? ['difl'] : []
        default:
          return []
      }
    },
    [selectedTooth],
  )

  const currentConditions = useMemo((): IDentalData[] => {
    if (!activeDentalChart || !selectedTooth) {
      return []
    }

    const dentalData = activeDentalChart.dental_data ?? []

    const result = getDentalDataForTooth(dentalData, selectedTooth)

    return result
  }, [activeDentalChart, selectedTooth])

  const conditionEntriesByCode = useMemo((): Record<string, IDentalData[]> => {
    return currentConditions.reduce<Record<string, IDentalData[]>>(
      (accumulator, condition) => {
        const code = condition.condition.toLowerCase()
        if (!accumulator[code]) {
          accumulator[code] = []
        }
        accumulator[code].push(condition)
        return accumulator
      },
      {},
    )
  }, [currentConditions])

  const conditionCodeSet = useMemo((): Set<string> => {
    return new Set(Object.keys(conditionEntriesByCode))
  }, [conditionEntriesByCode])
  const conditionPrefixSet = useMemo((): Set<string> => {
    const prefixes = new Set<string>()
    Object.keys(conditionEntriesByCode).forEach((conditionCode) => {
      const segments = conditionCode.split('_')
      for (let index = 1; index < segments.length; index += 1) {
        prefixes.add(segments.slice(0, index).join('_'))
      }
    })
    return prefixes
  }, [conditionEntriesByCode])

  const conditionTypeSetByCode = useMemo((): Record<string, Set<string>> => {
    return Object.entries(conditionEntriesByCode).reduce<
      Record<string, Set<string>>
    >((accumulator, [conditionCode, entries]) => {
      accumulator[conditionCode] = new Set(
        entries
          .map((entry) => entry.type?.toLowerCase())
          .filter((type): type is string => Boolean(type)),
      )
      return accumulator
    }, {})
  }, [conditionEntriesByCode])

  const conditionMaterialSetByCode = useMemo((): Record<
    string,
    Set<string>
  > => {
    return Object.entries(conditionEntriesByCode).reduce<
      Record<string, Set<string>>
    >((accumulator, [conditionCode, entries]) => {
      accumulator[conditionCode] = new Set(
        entries
          .map((entry) => entry.material?.toLowerCase())
          .filter((material): material is string => Boolean(material)),
      )
      return accumulator
    }, {})
  }, [conditionEntriesByCode])

  const hasConditionCode = useCallback(
    (conditionCode: string): boolean => {
      return conditionCodeSet.has(conditionCode.toLowerCase())
    },
    [conditionCodeSet],
  )

  const hasConditionType = useCallback(
    (conditionCode: string, typeCode: string): boolean => {
      const normalizedCondition = conditionCode.toLowerCase()
      const normalizedType = typeCode.toLowerCase()
      return (
        conditionTypeSetByCode[normalizedCondition]?.has(normalizedType) ??
        false
      )
    },
    [conditionTypeSetByCode],
  )

  const hasConditionMaterial = useCallback(
    (conditionCode: string, materialCode: string): boolean => {
      const normalizedCondition = conditionCode.toLowerCase()
      const normalizedMaterial = materialCode.toLowerCase()
      return (
        conditionMaterialSetByCode[normalizedCondition]?.has(
          normalizedMaterial,
        ) ?? false
      )
    },
    [conditionMaterialSetByCode],
  )

  const hasConditionOrPrefixed = useCallback(
    (conditionCode: string): boolean => {
      const normalizedCondition = conditionCode.toLowerCase()
      if (conditionCodeSet.has(normalizedCondition)) {
        return true
      }

      return conditionPrefixSet.has(normalizedCondition)
    },
    [conditionCodeSet, conditionPrefixSet],
  )
  const conditionHasSurfaceByCode = useMemo((): Record<string, boolean> => {
    return Object.entries(conditionEntriesByCode).reduce<
      Record<string, boolean>
    >((accumulator, [conditionCode, entries]) => {
      accumulator[conditionCode] = entries.some(
        (entry) => entry.surface != null && String(entry.surface).trim() !== '',
      )
      return accumulator
    }, {})
  }, [conditionEntriesByCode])
  const getConditionActiveState = useCallback(
    (conditionCode: string): boolean => {
      switch (conditionCode) {
        case 'recession':
        case 'periradicular':
        case 'crown':
        case 'veneer':
        case 'impacted':
        case 'fracture':
        case 'crack':
        case 'attrition':
        case 'missing':
          return hasConditionCode(conditionCode)
        case 'inlay':
        case 'onlay':
        case 'abrasion':
        case 'furcation':
          return conditionHasSurfaceByCode[conditionCode] ?? false
        case 'bridge':
          return hasConditionType('bridge', 'left_abutment') ||
            hasConditionType('bridge', 'right_abutment') ||
            hasConditionType('bridge', 'right_left_abutment')
            ? !hasConditionCode('abutment')
            : false
        case 'abutment':
          return (
            hasConditionType('abutment', 'left_abutment') ||
            hasConditionType('abutment', 'right_abutment') ||
            hasConditionType('abutment', 'right_left_abutment')
          )
        case 'caries':
        case 'recurring_caries':
        case 'erosion':
        case 'filling':
        case 'sealants':
          return hasConditionOrPrefixed(conditionCode)
        default:
          if (
            (EXCLUSIVE_CONDITIONS as readonly string[]).includes(conditionCode)
          ) {
            return hasConditionOrPrefixed(conditionCode)
          }
          return (
            hasConditionOrPrefixed(conditionCode) &&
            !hasConditionCode('missing')
          )
      }
    },
    [
      conditionHasSurfaceByCode,
      hasConditionCode,
      hasConditionOrPrefixed,
      hasConditionType,
    ],
  )

  // Check if in view mode (explicit history chart selected) - used across multiple functions
  const isViewMode = isExplicitHistorySelection

  // State to manage which sub-accordions are expanded for conditions
  const [expandedConditionsAccordions, setExpandedConditionsAccordions] =
    useState<string | null>(null)

  // State to manage which sub-accordions are expanded for orthodontics
  const [expandedOrthodonticsAccordions, setExpandedOrthodonticsAccordions] =
    useState<string | null>(null)

  const [xrayShowChartingInfo, setXrayShowChartingInfo] = useState(false)
  const [xraySelectedConditionCode, setXraySelectedConditionCode] = useState<
    string | null
  >(null)
  const [xrayOuterAccordionKey, setXrayOuterAccordionKey] =
    useState<string>('1')

  useEffect(() => {
    if (!voiceActive || preferChartingInfo) {
      return
    }
    setActiveAccordionKey(VOICE_ASSISTANT_ACCORDION_KEY)
    if (sidePanelLayout === 'xray-split') {
      setXrayShowChartingInfo(false)
      setXrayOuterAccordionKey(VOICE_ASSISTANT_ACCORDION_KEY)
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(
          '.charting-panel-container .accordion-item[data-voice-assistant="true"]',
        )
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return (): void => {
      window.cancelAnimationFrame(frame)
    }
  }, [voiceActive, sidePanelLayout, preferChartingInfo])

  useEffect(() => {
    if (chartingInfoFocusTick <= 0) {
      return
    }
    setPreferChartingInfo(true)
    setActiveAccordionKey(CHARTING_INFO_ACCORDION_KEY)
    if (sidePanelLayout === 'xray-split') {
      setXrayShowChartingInfo(true)
      setXraySelectedConditionCode(null)
      setXrayOuterAccordionKey('')
    }
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(
          '.charting-panel-container .accordion-item[data-charting-info="true"]',
        )
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return (): void => {
      window.cancelAnimationFrame(frame)
    }
  }, [chartingInfoFocusTick, sidePanelLayout])

  useEffect(() => {
    if (
      !validationErrors.patient_id &&
      !validationErrors.provider_id &&
      !validationErrors.title
    ) {
      setPreferChartingInfo(false)
    }
  }, [
    validationErrors.patient_id,
    validationErrors.provider_id,
    validationErrors.title,
  ])

  const resolvedAccordionKey = preferChartingInfo
    ? CHARTING_INFO_ACCORDION_KEY
    : activeAccordionKey
  const resolvedXrayOuterKey = preferChartingInfo ? '' : xrayOuterAccordionKey

  // Track previous conditions to detect new activations
  const prevConditionsRef = useRef<IDentalData[]>([])
  const prevSelectedToothRef = useRef<number | null>(null)

  // Auto-expand sub-accordions when conditions become active (only for newly activated conditions)
  useEffect(() => {
    const conditionsWithSubItems = [...PANEL_AUTO_EXPAND_CONDITION_CODES]

    const orthodonticsWithSubItems = [...ORTHODONTIC_CODES_WITH_SUB_ITEMS]

    // Helper to check if a condition is active
    const isConditionActive = (condition: string): boolean =>
      hasConditionOrPrefixed(condition)

    // Helper to check if a condition was active before
    const wasConditionActive = (condition: string): boolean => {
      return (
        prevConditionsRef.current.some((c) => c.condition === condition) ||
        prevConditionsRef.current.some((c) =>
          c.condition.toLowerCase().startsWith(`${condition}_`),
        )
      )
    }

    // Update conditions accordions: preserve user's manual state, only expand newly active ones
    setExpandedConditionsAccordions((prev) => {
      // Find the first newly activated condition
      const newlyActiveCondition = conditionsWithSubItems.find((condition) => {
        const isActive = isConditionActive(condition)
        const wasActive = wasConditionActive(condition)
        return isActive && !wasActive
      })

      // If a condition just became active, expand it (only one at a time)
      if (newlyActiveCondition) {
        return newlyActiveCondition
      }

      // If the currently expanded condition became inactive, close it
      if (prev) {
        const isStillActive = isConditionActive(prev)
        if (!isStillActive) {
          // Keep value/surface-driven accordions open so users can reselect
          // without losing context after deselecting the last active option.
          if (
            prev === 'caries' ||
            prev === 'recurring_caries' ||
            prev === 'filling' ||
            prev === 'erosion' ||
            prev === 'furcation' ||
            prev === 'fracture' ||
            prev === 'crown' ||
            prev === 'veneer' ||
            prev === 'abrasion' ||
            prev === 'attrition' ||
            prev === 'recession' ||
            prev === 'periradicular' ||
            prev === 'crack' ||
            prev === 'inlay' ||
            prev === 'onlay'
          ) {
            return prev
          }
          return null
        }
      }

      // Otherwise, preserve the current state (user's manual choice or no change)
      return prev
    })

    // Update orthodontics accordions: preserve user's manual state, only expand newly active ones
    setExpandedOrthodonticsAccordions((prev) => {
      // Find the first newly activated condition
      const newlyActiveCondition = orthodonticsWithSubItems.find(
        (condition) => {
          const isActive = isConditionActive(condition)
          const wasActive = wasConditionActive(condition)
          return isActive && !wasActive
        },
      )

      // If a condition just became active, expand it (only one at a time)
      if (newlyActiveCondition) {
        return newlyActiveCondition
      }

      // If the currently expanded condition became inactive, close it
      if (prev) {
        const isStillActive = isConditionActive(prev)
        if (!isStillActive) {
          // Endodontics + orthodontics share this expanded key. Keep accordions open
          // after deselecting severity/sub-items so users can pick another option.
          if (
            prev === 'periradicular' ||
            prev === 'impacted' ||
            prev === 'drifted'
          ) {
            return prev
          }
          return null
        }
      }

      // Otherwise, preserve the current state (user's manual choice or no change)
      return prev
    })

    // Update the ref for next comparison
    prevConditionsRef.current = currentConditions
  }, [currentConditions, hasConditionOrPrefixed])

  // Collapse all conditions when tooth selection changes for cleaner initial view
  useEffect(() => {
    if (!selectedTooth) {
      // Clear expanded accordions when no tooth is selected
      setExpandedConditionsAccordions(null)
      setExpandedOrthodonticsAccordions(null)
      return
    }

    // Collapse all accordions when a new tooth is selected for cleaner initial view
    setExpandedConditionsAccordions(null)
    setExpandedOrthodonticsAccordions(null)
    // Only run when selectedTooth changes, not when currentConditions changes
    // This prevents all accordions from opening when selecting sub-items
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTooth])
  useEffect(() => {
    setExpandedConditionsAccordions(null)
    setExpandedOrthodonticsAccordions(null)
  }, [activeChartId])

  useEffect(() => {
    if (sidePanelLayout !== 'xray-split') {
      return
    }
    setXrayShowChartingInfo(false)
    setXraySelectedConditionCode(null)
  }, [selectedTooth, activeChartId, sidePanelLayout])

  useEffect(() => {
    const stage = currentConditions.find(
      (c) =>
        (c.condition === 'bridge' || c.condition === 'abutment') &&
        c.material !== null,
    )
    const newStage = stage?.material
      ? (stage.material.toLowerCase() as
          | 'zirconia'
          | 'pfm'
          | 'pfz'
          | 'metal'
          | 'ceramic')
      : null
    setBridgeMaterialStage(newStage)
  }, [currentConditions])

  useEffect(() => {
    if (isViewMode || selectedTooth === null) {
      return
    }

    const toothStr = selectedTooth.toString()
    const toothBridgeRows = activeDentalChart.dental_data.filter(
      (data) =>
        String(data.tooth_number) === toothStr && data.condition === 'bridge',
    )

    const hasEmptyBridgeOnly = toothBridgeRows.some(
      (data) =>
        data.type === null &&
        data.material === null &&
        data.surface === null &&
        !data.recurrent,
    )
    const hasBridgeDetails = toothBridgeRows.some(
      (data) => data.type !== null || data.material !== null,
    )

    if (hasEmptyBridgeOnly && !hasBridgeDetails) {
      const updatedDentalData = activeDentalChart.dental_data.filter(
        (data) =>
          !(
            String(data.tooth_number) === toothStr &&
            data.condition === 'bridge'
          ),
      )
      dispatch(setDentalUpdated(true))
      commitActiveDentalChartUpdate({
        ...activeDentalChart,
        dental_data: updatedDentalData,
      })
    }
  }, [createDentalChart, dispatch, isViewMode, selectedTooth])

  useEffect(() => {
    if (isViewMode || selectedTooth === null) {
      return
    }

    // Rule: when user switches away from bridge/abutment and no connector is selected,
    // clear lingering bridge/abutment rows for the selected tooth.
    if (
      expandedConditionsAccordions === 'bridge' ||
      expandedConditionsAccordions === 'abutment'
    ) {
      return
    }

    const toothStr = selectedTooth.toString()
    const bridgeAbutmentRows = activeDentalChart.dental_data.filter(
      (data) =>
        String(data.tooth_number) === toothStr &&
        (data.condition === 'bridge' || data.condition === 'abutment'),
    )

    if (bridgeAbutmentRows.length === 0) {
      return
    }

    const hasConnectorSelected = bridgeAbutmentRows.some(
      (data) => data.type !== null,
    )
    if (hasConnectorSelected) {
      return
    }

    const updatedDentalData = activeDentalChart.dental_data.filter(
      (data) =>
        !(
          String(data.tooth_number) === toothStr &&
          (data.condition === 'bridge' || data.condition === 'abutment')
        ),
    )

    dispatch(setDentalUpdated(true))
    commitActiveDentalChartUpdate({
      ...activeDentalChart,
      dental_data: updatedDentalData,
    })
  }, [
    createDentalChart,
    dispatch,
    expandedConditionsAccordions,
    isViewMode,
    selectedTooth,
  ])

  const isNewDentalChart = !createDentalChart?.chart_id?.trim()

  const hasChartingValidationErrors = (): boolean => {
    return !!(
      (validationErrors.patient_id &&
        !selectedPatient &&
        !selectedDentalChart) ||
      (isChartProviderHardError(validationErrors.provider_id) &&
        !isPersistedChart &&
        isNewDentalChart) ||
      (validationErrors.title && !isPersistedChart && isNewDentalChart)
    )
  }

  // Handle furcation area selection
  const handleFurcationAreaClick = (areaCode: string): void => {
    if (!selectedTooth || isViewMode) return

    // Find the furcation condition with a type
    const furcationCondition = currentConditions.find(
      (c) => c.condition === 'furcation' && c.type,
    )

    if (!furcationCondition || !furcationCondition.type) {
      return
    }

    dispatch(setDentalUpdated(true))

    // Get current areas for this furcation (null = all available; '' = none)
    const currentAreas = getSelectedFurcationAreas(
      furcationCondition.surface,
      selectedTooth,
    )

    // Toggle the area
    let newAreas: string[]
    if (currentAreas.includes(areaCode)) {
      newAreas = currentAreas.filter((a) => a !== areaCode)
    } else {
      newAreas = [...currentAreas, areaCode]
    }

    const currentDentalData = activeDentalChart.dental_data ?? []
    const toothNumberStr = selectedTooth.toString()

    if (newAreas.length === 0) {
      commitActiveDentalChartUpdate({
        ...activeDentalChart,
        dental_data: removeAllConditionRowsForTooth(
          currentDentalData,
          selectedTooth,
          'furcation',
        ),
      })
      return
    }

    const updatedDentalData = currentDentalData.map((data) => {
      const dataToothNumber =
        typeof data.tooth_number === 'string'
          ? data.tooth_number
          : String(data.tooth_number)

      if (
        dataToothNumber === toothNumberStr &&
        data.condition === 'furcation' &&
        data.type?.toLowerCase() === furcationCondition.type?.toLowerCase()
      ) {
        return {
          ...data,
          surface: newAreas.join(','),
        }
      }
      return data
    })

    commitActiveDentalChartUpdate({
      ...activeDentalChart,
      dental_data: updatedDentalData,
    })
  }

  const handleSurfaceClick = (surface: string, conditionType: string): void => {
    if (!selectedTooth) return

    const fractureSurfaceCode =
      conditionType === 'fracture'
        ? FRACTURE_SURFACE_LABEL_CODE_MAP[surface]
        : undefined
    const surfaceCode = fractureSurfaceCode ?? SURFACE_LABEL_MAP[surface]

    if (!surfaceCode) return

    const hasRegion =
      REGION_DEPENDENT_CONDITIONS.some((c) => hasConditionCode(c)) ||
      hasConditionCode('caries') ||
      hasConditionCode('recurring_caries') ||
      conditionType === 'caries' ||
      conditionType === 'recurring_caries' ||
      hasConditionCode('filling') ||
      conditionType === 'filling' ||
      hasConditionCode('abrasion') ||
      hasConditionCode('sealants')

    const requiresRegion =
      conditionType !== 'erosion' &&
      conditionType !== 'fracture' &&
      conditionType !== 'abrasion'

    if (requiresRegion && !hasRegion) {
      setShowConditionWarning(true)
      return
    }

    if (conditionType === 'erosion') {
      onApplyConditionPayload(
        toPayload({
          condition: 'erosion',
          surface: wearSurfaceFromGridCode(surfaceCode),
        }),
      )
      return
    }

    if (conditionType === 'abrasion') {
      const existingAbrasionType = currentConditions.find(
        (condition) =>
          condition.condition === 'abrasion' && condition.type !== null,
      )?.type
      if (!existingAbrasionType) {
        setShowConditionWarning(true)
        return
      }
      onApplyConditionPayload(
        toPayload({
          condition: 'abrasion',
          type: existingAbrasionType,
          surface: wearSurfaceFromGridCode(surfaceCode),
        }),
      )
      return
    }

    if (conditionType === 'inlay' || conditionType === 'onlay') {
      if (INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)) {
        const label = conditionType === 'inlay' ? 'Inlays' : 'Onlays'
        alert(
          `${label} are not applicable to Tooth ${selectedTooth}. ${label} are only applicable to posterior teeth (molars and premolars): Teeth 1-5, 12-16, 17-21, and 28-32.`,
        )
        return
      }

      const selectedSurfaces = new Set(
        currentConditions
          .filter((c) => c.condition === conditionType && c.surface !== null)
          .map((c) => c.surface as string),
      )
      const { toAdd, toRemove } = getSurfaceToggleDiff(
        surfaceCode,
        selectedSurfaces,
        shouldAutoImplyCuspsForTooth(selectedTooth),
      )
      const conditionPayloads = [
        ...toRemove.map((surfaceToToggle) =>
          toPayload({ condition: conditionType, surface: surfaceToToggle }),
        ),
        ...toAdd.map((surfaceToToggle) =>
          toPayload({ condition: conditionType, surface: surfaceToToggle }),
        ),
      ]

      if (conditionPayloads.length > 0) {
        onApplyConditionPayload(conditionPayloads)
      }
      return
    }

    if (conditionType === 'drifted') {
      const allowedSurfaces = ['b', 'm', 'd', 'p', 'l'] // Facial, Mesial, Distal, Palatal/Lingual
      if (!allowedSurfaces.includes(surfaceCode)) {
        alert(
          `Drifted condition is only applicable to Facial, Mesial, Distal, and Palatal/Lingual surfaces.`,
        )
        return
      }

      onApplyConditionPayload(
        toPayload({ condition: 'drifted', surface: surfaceCode }),
      )
      return
    }

    if (conditionType === 'filling') {
      const materialPayloads: ApplyConditionPayload[] = currentConditions.some(
        (c) =>
          c.condition === 'filling' &&
          c.material != null &&
          String(c.material).trim() !== '',
      )
        ? []
        : [toPayload({ condition: 'filling', material: fillingMaterialDraft })]

      const selectedSurfaces = new Set(
        currentConditions
          .filter((c) => c.condition === 'filling' && c.surface !== null)
          .map((c) => c.surface as string),
      )
      const { toAdd, toRemove } = getSurfaceToggleDiff(
        surfaceCode,
        selectedSurfaces,
        shouldAutoImplyCuspsForTooth(selectedTooth),
      )
      const conditionPayloads = [
        ...toRemove.map((surfaceToToggle) =>
          toPayload({ condition: 'filling', surface: surfaceToToggle }),
        ),
        ...toAdd.map((surfaceToToggle) =>
          toPayload({ condition: 'filling', surface: surfaceToToggle }),
        ),
      ]

      if (conditionPayloads.length > 0 || materialPayloads.length > 0) {
        onApplyConditionPayload([...materialPayloads, ...conditionPayloads])
      }
      return
    }

    if (conditionType === 'fracture') {
      const fractureTypes = getFractureTypesForSurface(surfaceCode)
      if (fractureTypes.length === 0) {
        return
      }
      const selectedFractureTypes = new Set(
        currentConditions
          .filter((c) => c.condition === 'fracture' && c.type !== null)
          .map((c) => c.type!.toLowerCase()),
      )
      const allSelected = fractureTypes.every((type) =>
        selectedFractureTypes.has(type),
      )
      const fracturePayloads = allSelected
        ? fractureTypes
            .filter((type) => selectedFractureTypes.has(type))
            .map((type) => toPayload({ condition: 'fracture', type }))
        : fractureTypes
            .filter((type) => !selectedFractureTypes.has(type))
            .map((type) => toPayload({ condition: 'fracture', type }))

      if (fracturePayloads.length > 0) {
        onApplyConditionPayload(fracturePayloads)
      }
      return
    }

    if (conditionType === 'caries' || conditionType === 'recurring_caries') {
      const targetCondition = conditionType
      // Default to D1 so caries can be applied from surface selection
      // even before an explicit type click.
      const cariesType = currentConditions.find(
        (c) =>
          c.condition === targetCondition &&
          c.type !== null &&
          ['d1', 'd2', 'd3', 'e1', 'e2'].includes(c.type.toLowerCase()),
      )
      const cariesTypeCode = (cariesType?.type ?? 'd1').toLowerCase()

      const selectedSurfaces = new Set(
        currentConditions
          .filter(
            (c) =>
              c.condition === targetCondition &&
              c.type?.toLowerCase() === cariesTypeCode &&
              c.surface !== null,
          )
          .map((c) => c.surface as string),
      )
      const { toAdd, toRemove } = getSurfaceToggleDiff(
        surfaceCode,
        selectedSurfaces,
        shouldAutoImplyCuspsForTooth(selectedTooth),
      )
      const conditionPayloads = [
        ...toRemove.map((surfaceToToggle) =>
          toPayload({
            condition: targetCondition,
            type: cariesTypeCode,
            surface: surfaceToToggle,
          }),
        ),
        ...toAdd.map((surfaceToToggle) =>
          toPayload({
            condition: targetCondition,
            type: cariesTypeCode,
            surface: surfaceToToggle,
          }),
        ),
      ]

      if (conditionPayloads.length > 0) {
        onApplyConditionPayload(conditionPayloads)
      }
      return
    }

    onApplyConditionPayload(
      toPayload({ condition: conditionType, surface: surfaceCode }),
    )
  }

  // Render surface selection grid for a specific condition
  const renderSurfaceSelection = (conditionType: string): React.ReactNode => {
    const normalizedConditionType = conditionType.toLowerCase()
    const hasCondition = hasConditionOrPrefixed(normalizedConditionType)
    const entriesForCondition =
      conditionEntriesByCode[normalizedConditionType] ?? []

    // Erosion only has surfaces (no type/material), so always show surfaces when accordion is expanded
    const isSurfaceOnlyCondition =
      conditionType === 'erosion' ||
      conditionType === 'caries' ||
      conditionType === 'recurring_caries' ||
      conditionType === 'filling' ||
      conditionType === 'fracture'
    if (!hasCondition && !isSurfaceOnlyCondition) return null

    const isAnteriorFacialTooth =
      selectedTooth !== null &&
      ((selectedTooth >= 6 && selectedTooth <= 11) ||
        (selectedTooth >= 22 && selectedTooth <= 27))

    const labialLabel = isAnteriorFacialTooth ? 'Facial' : 'Buccal'
    const middleErosionLabel = isAnteriorFacialTooth ? 'Incisal' : 'Occlusal'

    const erosionSurfaceRows =
      selectedTooth === null
        ? [[labialLabel, 'Occlusal', 'Palatal'], ['Lingual']]
        : selectedTooth >= 1 && selectedTooth <= 16
          ? [[labialLabel, middleErosionLabel, 'Palatal']]
          : [['Lingual', middleErosionLabel, labialLabel]]

    const isUpperAnterior =
      selectedTooth !== null && INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth)
    const isLowerAnterior =
      selectedTooth !== null &&
      LOWER_INCISOR_CANINE_TOOTH_IDS.includes(selectedTooth)

    const fractureSurfaceRows =
      selectedTooth === null
        ? [
            ['MOB', 'DOB'],
            ['MOP', 'DOP'],
          ]
        : isUpperAnterior
          ? [['MIFP', 'Incisal', 'DIFP']]
          : isLowerAnterior
            ? [['MIFL', 'Incisal', 'DIFL']]
            : PREMOLAR_TOOTH_IDS.includes(selectedTooth) ||
                MOLAR_TOOTH_IDS.includes(selectedTooth)
              ? [
                  ['MOB', 'DOB'],
                  ['MOP', 'DOP'],
                ]
              : LOWER_PREMOLAR_TOOTH_IDS.includes(selectedTooth) ||
                  LOWER_MOLAR_TOOTH_IDS.includes(selectedTooth)
                ? [
                    ['MOB', 'DOB'],
                    ['MOL', 'DOL'],
                  ]
                : getSurfaceRows(selectedTooth, currentConditions)

    const surfaceRows =
      conditionType === 'erosion'
        ? erosionSurfaceRows
        : conditionType === 'abrasion'
          ? getAbrasionSurfaceRows(selectedTooth)
          : conditionType === 'fracture'
            ? fractureSurfaceRows
            : getSurfaceRows(selectedTooth, currentConditions)

    return (
      <div className="surface-section p-0">
        {surfaceRows.map((row, i) => (
          <div key={i} className="row-wrap">
            {row.map((label) => {
              const displayLabel = getSurfaceLabelAbbreviation(label)
              const fractureSurfaceCode =
                conditionType === 'fracture'
                  ? FRACTURE_SURFACE_LABEL_CODE_MAP[label]
                  : undefined
              const surfaceCode =
                fractureSurfaceCode ?? SURFACE_LABEL_MAP[label]
              const code = surfaceCode?.toLowerCase()

              if (!code || !surfaceCode) return null
              if (
                conditionType === 'fracture' &&
                getFractureTypesForSurface(surfaceCode).length === 0
              ) {
                return null
              }
              if (
                ['inlay', 'onlay'].includes(conditionType) &&
                [
                  'Cervical Buccal',
                  'Cervical Facial',
                  'Cervical Palatal',
                  'Cervical Lingual',
                ].includes(label)
              ) {
                return null
              }
              if (
                conditionType === 'inlay' &&
                ['Buccal', 'Facial', 'Palatal', 'Lingual'].includes(label)
              ) {
                return null
              }

              // Determine if this surface is active for this condition
              let isActive = false
              const selectedSurfaceCodes = new Set(
                entriesForCondition
                  .filter((c) => c.surface)
                  .map((c) => String(c.surface).toLowerCase()),
              )

              if (
                conditionType === 'caries' ||
                conditionType === 'recurring_caries'
              ) {
                isActive = selectedSurfaceCodes.has(code)
              } else if (conditionType === 'filling') {
                isActive = selectedSurfaceCodes.has(code)
              } else if (conditionType === 'erosion') {
                const erosionSurfaceCode = wearSurfaceFromGridCode(code)
                isActive = entriesForCondition.some((c) =>
                  storedWearSurfaceMatches(c.surface, erosionSurfaceCode),
                )
              } else if (conditionType === 'abrasion') {
                const abrasionSurfaceCode = wearSurfaceFromGridCode(code)
                isActive = entriesForCondition.some(
                  (c) =>
                    c.condition === 'abrasion' &&
                    storedWearSurfaceMatches(c.surface, abrasionSurfaceCode),
                )
              } else if (
                conditionType === 'inlay' ||
                conditionType === 'onlay'
              ) {
                isActive = selectedSurfaceCodes.has(code)
              } else if (conditionType === 'drifted') {
                isActive = entriesForCondition.some((c) => {
                  if (c.condition === 'drifted' && c.surface) {
                    const surfaceCodeFromCondition = c.surface.toLowerCase()
                    // Match exact surface code or handle compound surfaces (case-insensitive)
                    return (
                      surfaceCodeFromCondition === code ||
                      surfaceCodeFromCondition.startsWith(`${code}_`) ||
                      code.startsWith(`${surfaceCodeFromCondition}_`)
                    )
                  }
                  return false
                })
              } else if (conditionType === 'fracture') {
                const fractureTypes = getFractureTypesForSurface(surfaceCode)
                isActive =
                  fractureTypes.length > 0 &&
                  fractureTypes.every((type) =>
                    entriesForCondition.some(
                      (c) =>
                        c.condition === 'fracture' &&
                        c.type?.toLowerCase() === type,
                    ),
                  )
              }

              // Check if surface should be disabled for this condition
              let isDisabled = false
              // Filling surfaces are not disabled - allow all surfaces for filling (same as caries)
              if (conditionType === 'drifted') {
                const allowedSurfaces = ['b', 'm', 'd', 'p', 'l']
                isDisabled = !allowedSurfaces.includes(
                  surfaceCode.toLowerCase(),
                )
              } else if (
                conditionType === 'erosion' ||
                conditionType === 'abrasion'
              ) {
                isDisabled = false
              } else if (
                conditionType === 'inlay' ||
                conditionType === 'onlay'
              ) {
                const isCervicalBuccalSurface =
                  surfaceCode.toLowerCase() === 'cb'
                isDisabled = isCervicalBuccalSurface
              } else if (conditionType === 'fracture') {
                isDisabled =
                  getFractureTypesForSurface(surfaceCode).length === 0
              }

              return (
                <div
                  key={label}
                  className={classNames('condition-box', {
                    active: isActive,
                    disabled: isDisabled || isViewMode,
                    'erosion-surface-box':
                      conditionType === 'erosion' ||
                      conditionType === 'abrasion',
                  })}
                  onClick={() => {
                    if (!selectedTooth || isDisabled || isViewMode) {
                      return
                    }
                    handleSurfaceClick(label, conditionType)
                  }}
                  title={getSurfaceTooltipTitle(label)}
                >
                  {displayLabel}
                </div>
              )
            })}
          </div>
        ))}
      </div>
    )
  }

  // Helper function to render sub-items grouped in accordion body
  const renderConditionSubItems = (conditionCode: string): React.ReactNode => {
    const hasSubItems = conditionHasSubItems(conditionCode)
    if (!hasSubItems) return null

    const sections: React.ReactNode[] = []

    // Render Type/Stage selections
    if (conditionCode === 'furcation') {
      const handleFurcationTypeClick = (stage: string): void => {
        if (!selectedTooth || isViewMode) return

        dispatch(setDentalUpdated(true))

        // Get available areas for this tooth
        const availableAreas = getAvailableFurcationAreas(selectedTooth)
        const areasString =
          availableAreas.length > 0 ? availableAreas.join(',') : null

        // Update the dental data to add furcation with type and auto-selected areas
        const currentDentalData = activeDentalChart.dental_data ?? []
        const toothNumberStr = selectedTooth.toString()

        // Check if furcation already exists for this tooth
        const existingFurcationIndex = currentDentalData.findIndex((data) => {
          const dataToothNumber =
            typeof data.tooth_number === 'string'
              ? data.tooth_number
              : String(data.tooth_number)
          return (
            dataToothNumber === toothNumberStr && data.condition === 'furcation'
          )
        })

        let updatedDentalData: IDentalData[]
        if (existingFurcationIndex >= 0) {
          // Update existing furcation entry
          updatedDentalData = currentDentalData.map((data, index) => {
            if (index === existingFurcationIndex) {
              return {
                ...data,
                type: stage.toLowerCase(),
                surface: areasString,
              }
            }
            return data
          })
        } else {
          // Add new furcation entry
          const newFurcationEntry: IDentalData = {
            tooth_number: toothNumberStr,
            condition: 'furcation',
            type: stage.toLowerCase(),
            material: null,
            surface: areasString,
            recurrent: false,
          }
          updatedDentalData = [...currentDentalData, newFurcationEntry]
        }

        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData,
        })
      }

      sections.push(
        <div key="furcation-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Type</label>
          <div className="d-flex flex-nowrap" style={{ gap: '4px' }}>
            {FURCATION_TYPE_CODES.map((stage) => {
              const isChecked = hasConditionType('furcation', stage)
              const classLabel = `Class ${stage.replace(/^C/i, '')}`

              return (
                <Button
                  key={stage}
                  size="sm"
                  aria-pressed={isChecked}
                  disabled={isViewMode}
                  title={classLabel}
                  onClick={() => {
                    if (isViewMode) return
                    if (!isChecked) {
                      handleFurcationTypeClick(stage)
                    }
                  }}
                  className={classNames(
                    chartingSidepanelToggleClass(isChecked),
                    'flex-fill',
                  )}
                >
                  {stage}
                </Button>
              )
            })}
          </div>
        </div>,
      )

      // Add Areas section for furcation
      const useFacialForFurcation =
        selectedTooth !== null &&
        ((selectedTooth >= 6 && selectedTooth <= 11) ||
          (selectedTooth >= 22 && selectedTooth <= 27))
      const labialLabel = useFacialForFurcation ? 'Facial' : 'Buccal'
      const mesioLabialLabel = useFacialForFurcation
        ? 'Mesiofacial'
        : 'Mesiobuccal'
      const distoLabialLabel = useFacialForFurcation
        ? 'Distofacial'
        : 'Distobuccal'
      const furcationAreas = [
        { code: 'buccal-distal', label: distoLabialLabel },
        { code: 'buccal-mesial', label: mesioLabialLabel },
        { code: 'buccal', label: labialLabel },
        { code: 'palatal', label: 'Palatal' },
        { code: 'lingual', label: 'Lingual' },
      ]

      const availableAreas = getAvailableFurcationAreas(selectedTooth)

      // Only show Areas section if a furcation type is selected
      const hasFurcationType = (conditionEntriesByCode.furcation ?? []).some(
        (condition) => Boolean(condition.type),
      )

      if (hasFurcationType) {
        const availableFurcationAreas = furcationAreas.filter(({ code }) =>
          availableAreas.includes(code),
        )
        sections.push(
          <div key="furcation-areas" className="mt-2 px-1">
            <label className="form-label mb-1 fw-bold">Surfaces</label>
            <div className="surface-section p-0">
              <div className="row-wrap">
                {availableFurcationAreas.map(({ code, label }) => {
                  const isChecked = (
                    conditionEntriesByCode.furcation ?? []
                  ).some((condition) =>
                    getSelectedFurcationAreas(
                      condition.surface,
                      selectedTooth,
                    ).includes(code),
                  )

                  const displayLabel = getSurfaceLabelAbbreviation(label)
                  const isDisabled = isViewMode

                  return (
                    <div
                      key={code}
                      className={classNames('condition-box', {
                        active: isChecked,
                        disabled: isDisabled,
                      })}
                      onClick={() => {
                        if (isDisabled) return
                        handleFurcationAreaClick(code)
                      }}
                      title={getSurfaceTooltipTitle(label)}
                    >
                      {displayLabel}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>,
        )
      }
    }

    if (conditionCode === 'recession') {
      const recessionClasses = [
        { value: 'class1', label: 'Class 1' },
        { value: 'class2', label: 'Class 2' },
        { value: 'class3', label: 'Class 3' },
        { value: 'class4', label: 'Class 4' },
      ]

      sections.push(
        <div key="recession-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Type</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <ButtonGroup className="d-flex" style={{ gap: '4px' }}>
              {recessionClasses
                .slice(0, 2)
                .map(({ value, label: classLabel }) => {
                  const stageCondition = toPayload({
                    condition: 'recession',
                    type: value,
                  })
                  const isChecked = hasConditionType('recession', value)

                  return (
                    <Button
                      key={value}
                      size="sm"
                      aria-pressed={isChecked}
                      disabled={isViewMode}
                      onClick={() => {
                        if (isViewMode) return
                        if (isChecked && selectedTooth !== null) {
                          const updatedDentalData =
                            removeAllConditionRowsForTooth(
                              activeDentalChart.dental_data,
                              selectedTooth,
                              'recession',
                            )
                          dispatch(setDentalUpdated(true))
                          commitActiveDentalChartUpdate({
                            ...activeDentalChart,
                            dental_data: updatedDentalData,
                          })
                          return
                        }
                        onApplyConditionPayload(stageCondition)
                      }}
                      className={chartingSidepanelToggleClass(isChecked)}
                    >
                      {classLabel}
                    </Button>
                  )
                })}
            </ButtonGroup>
            <ButtonGroup className="d-flex" style={{ gap: '4px' }}>
              {recessionClasses
                .slice(2, 4)
                .map(({ value, label: classLabel }) => {
                  const stageCondition = toPayload({
                    condition: 'recession',
                    type: value,
                  })
                  const isChecked = hasConditionType('recession', value)

                  return (
                    <Button
                      key={value}
                      size="sm"
                      aria-pressed={isChecked}
                      disabled={isViewMode}
                      onClick={() => {
                        if (isViewMode) return
                        if (isChecked && selectedTooth !== null) {
                          const updatedDentalData =
                            removeAllConditionRowsForTooth(
                              activeDentalChart.dental_data,
                              selectedTooth,
                              'recession',
                            )
                          dispatch(setDentalUpdated(true))
                          commitActiveDentalChartUpdate({
                            ...activeDentalChart,
                            dental_data: updatedDentalData,
                          })
                          return
                        }
                        onApplyConditionPayload(stageCondition)
                      }}
                      className={chartingSidepanelToggleClass(isChecked)}
                    >
                      {classLabel}
                    </Button>
                  )
                })}
            </ButtonGroup>
          </div>
        </div>,
      )
    }

    if (conditionCode === 'periradicular') {
      sections.push(
        <div key="periradicular-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Severity</label>
          <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {['slight', 'moderate', 'severe'].map((severity) => {
              const severityCondition = toPayload({
                condition: 'periradicular',
                type: severity,
              })
              const isChecked = hasConditionType('periradicular', severity)

              return (
                <Button
                  key={severity}
                  size="sm"
                  aria-pressed={isChecked}
                  disabled={isViewMode}
                  onClick={() => {
                    if (isViewMode) return
                    if (isChecked && selectedTooth !== null) {
                      const updatedDentalData = removeAllConditionRowsForTooth(
                        activeDentalChart.dental_data,
                        selectedTooth,
                        'periradicular',
                      )
                      dispatch(setDentalUpdated(true))
                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                      return
                    }
                    onApplyConditionPayload(severityCondition)
                  }}
                  className={chartingSidepanelToggleClass(isChecked)}
                >
                  {capitalizeFirstLetter(severity)}
                </Button>
              )
            })}
          </ButtonGroup>
        </div>,
      )
    }

    if (conditionCode === 'crack') {
      const isLowerArch =
        !!selectedTooth && selectedTooth >= 17 && selectedTooth <= 32
      const isAnteriorTooth =
        !!selectedTooth &&
        ((selectedTooth >= 6 && selectedTooth <= 11) ||
          (selectedTooth >= 22 && selectedTooth <= 27))
      const sideLabel = isLowerArch ? 'Lingual' : 'Palatal'
      const labialLabel = isAnteriorTooth ? 'Facial' : 'Buccal'
      const middleLabel = isAnteriorTooth ? 'Incisal' : 'Occlusal'
      const crackTypeRows: Array<{
        label: string
        verticalType: string
        horizontalType: string
      }> = [
        {
          label: 'Root',
          verticalType: 'vertical_root',
          horizontalType: 'horizontal_root',
        },
        {
          label: labialLabel,
          verticalType: 'vertical_crown_buccal',
          horizontalType: 'horizontal_crown_buccal',
        },
        {
          label: sideLabel,
          verticalType: isLowerArch
            ? 'vertical_crown_lingual'
            : 'vertical_crown_palatal',
          horizontalType: isLowerArch
            ? 'horizontal_crown_lingual'
            : 'horizontal_crown_palatal',
        },
        {
          label: middleLabel,
          verticalType: 'vertical_crown_occlusal',
          horizontalType: 'horizontal_crown_occlusal',
        },
      ]
      sections.push(
        <div key="crack-type" className="mt-2 px-1">
          <div className="d-flex flex-column" style={{ gap: '6px' }}>
            {crackTypeRows.map((row) => {
              const options = [
                { label: 'Vertical', type: row.verticalType },
                { label: 'Horizontal', type: row.horizontalType },
              ]
              return (
                <div key={row.label} className="d-flex flex-column">
                  <div
                    style={{
                      fontSize: '12px',
                      fontWeight: 600,
                      color: 'var(--blue-700)',
                    }}
                  >
                    {row.label}
                  </div>
                  <ButtonGroup
                    className="d-flex flex-grow-1 ps-1"
                    style={{ gap: '4px' }}
                  >
                    {options.map((option) => {
                      const isChecked = hasConditionType('crack', option.type)
                      const activeCrackTypes = currentConditions.filter(
                        (condition) =>
                          condition.condition === 'crack' &&
                          condition.type !== null &&
                          String(condition.type).trim() !== '',
                      )
                      return (
                        <Button
                          key={option.type}
                          size="sm"
                          aria-pressed={isChecked}
                          disabled={isViewMode}
                          className={chartingSidepanelToggleClass(
                            isChecked,
                            'crack',
                          )}
                          onClick={() => {
                            if (isViewMode) return

                            if (
                              isChecked &&
                              selectedTooth !== null &&
                              activeCrackTypes.length <= 1
                            ) {
                              const updatedDentalData =
                                removeAllConditionRowsForTooth(
                                  activeDentalChart.dental_data,
                                  selectedTooth,
                                  'crack',
                                )
                              dispatch(setDentalUpdated(true))
                              commitActiveDentalChartUpdate({
                                ...activeDentalChart,
                                dental_data: updatedDentalData,
                              })
                              return
                            }

                            onApplyConditionPayload(
                              toPayload({
                                condition: 'crack',
                                type: option.type,
                              }),
                            )
                          }}
                        >
                          {option.label}
                        </Button>
                      )
                    })}
                  </ButtonGroup>
                </div>
              )
            })}
          </div>
        </div>,
      )
    }

    if (conditionCode === 'fracture') {
      const fractureSurfaceContent = renderSurfaceSelection('fracture')
      if (fractureSurfaceContent) {
        sections.push(
          <div key="fracture-surfaces" className="mt-2 px-1">
            <label className="form-label mb-1 fw-bold">Surfaces</label>
            {fractureSurfaceContent}
          </div>,
        )
      }
    }

    if (conditionCode === '__legacy_fracture_types_hidden__') {
      sections.push(
        <div key="fracture-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Type</label>
          <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {(() => {
              const fractureTypes = getFractureTypesForTooth(selectedTooth)

              return fractureTypes.map((stage) => {
                const isChecked = hasConditionType('fracture', stage)
                const acronym = getFractureAcronym(stage)
                const fullName = getFractureFullName(stage)

                return (
                  <OverlayTrigger
                    {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                    key={stage}
                    placement="top"
                    overlay={renderFractureTooltip(fullName)}
                  >
                    <Button
                      size="sm"
                      aria-pressed={isChecked}
                      disabled={isViewMode}
                      className={chartingSidepanelToggleClass(
                        isChecked,
                        'half',
                      )}
                      onClick={() => {
                        if (isViewMode) return
                        onApplyConditionPayload(
                          toPayload({ condition: 'fracture', type: stage }),
                        )
                      }}
                    >
                      {acronym}
                    </Button>
                  </OverlayTrigger>
                )
              })
            })()}
          </ButtonGroup>
        </div>,
      )
    }

    if (conditionCode === 'attrition') {
      sections.push(
        <div key="attrition-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Severity</label>
          <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {['slight', 'moderate', 'severe'].map((severity) => {
              const severityCondition = toPayload({
                condition: 'attrition',
                type: severity,
              })
              const isChecked = hasConditionType('attrition', severity)

              return (
                <Button
                  key={severity}
                  size="sm"
                  aria-pressed={isChecked}
                  disabled={isViewMode}
                  onClick={() => {
                    if (isViewMode) return
                    if (isChecked && selectedTooth !== null) {
                      const updatedDentalData = removeAllConditionRowsForTooth(
                        activeDentalChart.dental_data,
                        selectedTooth,
                        'attrition',
                      )
                      dispatch(setDentalUpdated(true))
                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                      return
                    }
                    onApplyConditionPayload(severityCondition)
                  }}
                  className={chartingSidepanelToggleClass(isChecked)}
                >
                  {capitalizeFirstLetter(severity)}
                </Button>
              )
            })}
          </ButtonGroup>
        </div>,
      )
    }

    if (conditionCode === 'abrasion') {
      sections.push(
        <div key="abrasion-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Severity</label>
          <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {['slight', 'moderate', 'severe'].map((severity) => {
              const severityCondition = toPayload({
                condition: 'abrasion',
                type: severity,
              })
              const isChecked = hasConditionType('abrasion', severity)

              return (
                <Button
                  key={severity}
                  size="sm"
                  aria-pressed={isChecked}
                  disabled={isViewMode}
                  onClick={() => {
                    if (isViewMode) return
                    if (isChecked && selectedTooth !== null) {
                      const updatedDentalData = removeAllConditionRowsForTooth(
                        activeDentalChart.dental_data,
                        selectedTooth,
                        'abrasion',
                      )
                      dispatch(setDentalUpdated(true))
                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                      return
                    }
                    onApplyConditionPayload(severityCondition)
                  }}
                  className={chartingSidepanelToggleClass(isChecked)}
                >
                  {capitalizeFirstLetter(severity)}
                </Button>
              )
            })}
          </ButtonGroup>
        </div>,
      )
    }

    if (conditionCode === 'bridge' || conditionCode === 'abutment') {
      const selectedAbutmentType = currentConditions.find(
        (condition) =>
          (condition.condition === 'abutment' ||
            condition.condition === 'bridge') &&
          condition.type,
      )?.type
      const hasMesialConnector =
        selectedAbutmentType === 'left_abutment' ||
        selectedAbutmentType === 'right_left_abutment'
      const hasDistalConnector =
        selectedAbutmentType === 'right_abutment' ||
        selectedAbutmentType === 'right_left_abutment'

      const connectorTypeFromState = (
        hasMesial: boolean,
        hasDistal: boolean,
      ): 'left_abutment' | 'right_abutment' | 'right_left_abutment' | null => {
        if (hasMesial && hasDistal) return 'right_left_abutment'
        if (hasMesial) return 'left_abutment'
        if (hasDistal) return 'right_abutment'
        return null
      }

      if (conditionCode === 'abutment' || conditionCode === 'bridge') {
        sections.push(
          <div key="bridge-type" className="mt-2 px-1">
            <label className="form-label mb-1 fw-bold">Connectors</label>
            <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
              {[
                { key: 'mesial', label: 'Mesial', checked: hasMesialConnector },
                { key: 'distal', label: 'Distal', checked: hasDistalConnector },
              ].map((connector) => {
                const isChecked = connector.checked

                return (
                  <Button
                    key={connector.key}
                    size="sm"
                    aria-pressed={isChecked}
                    disabled={isViewMode}
                    className={chartingSidepanelToggleClass(isChecked)}
                    onClick={() => {
                      if (isViewMode) return
                      if (selectedTooth === null) return
                      const material = bridgeMaterialStage || 'zirconia'
                      setBridgeMaterialStage(material)
                      let updatedDentalData = activeDentalChart.dental_data

                      const nextMesial =
                        connector.key === 'mesial'
                          ? !hasMesialConnector
                          : hasMesialConnector
                      const nextDistal =
                        connector.key === 'distal'
                          ? !hasDistalConnector
                          : hasDistalConnector
                      const nextType = connectorTypeFromState(
                        nextMesial,
                        nextDistal,
                      )

                      updatedDentalData = updatedDentalData.filter(
                        (data) =>
                          !(
                            String(data.tooth_number) ===
                              selectedTooth.toString() &&
                            (data.condition === 'abutment' ||
                              data.condition === 'bridge')
                          ),
                      )

                      const connectorCondition =
                        conditionCode === 'bridge' ? 'bridge' : 'abutment'
                      if (nextType) {
                        updatedDentalData = addConditionToDentalData(
                          updatedDentalData,
                          selectedTooth,
                          connectorCondition,
                          nextType,
                          material,
                          null,
                          false,
                        )
                      } else {
                        // Keep the selected condition/material even when both connectors are off.
                        updatedDentalData = addConditionToDentalData(
                          updatedDentalData,
                          selectedTooth,
                          connectorCondition,
                          null,
                          material,
                          null,
                          false,
                        )
                      }

                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                      dispatch(setDentalUpdated(true))
                    }}
                  >
                    {connector.label}
                  </Button>
                )
              })}
            </ButtonGroup>
          </div>,
        )
      }

      if (conditionCode === 'bridge' || conditionCode === 'abutment') {
        sections.push(
          <div key="bridge-material" className="mt-2 px-1">
            <label className="form-label mb-1 fw-bold">Material</label>
            <div className="d-flex gap-2 flex-wrap">
              {[
                { value: 'zirconia', label: 'Zirconia' },
                { value: 'pfm', label: 'PFM' },
                { value: 'pfz', label: 'PFZ' },
                { value: 'metal', label: 'Metal' },
                { value: 'ceramic', label: 'Ceramic' },
              ].map((bridgeType) => {
                const isChecked =
                  hasConditionMaterial(conditionCode, bridgeType.value) ||
                  (conditionCode === 'bridge' &&
                    hasConditionMaterial('abutment', bridgeType.value))
                const chartColor = getChartMaterialColor(
                  bridgeType.value,
                  'bridge',
                )

                return (
                  <OverlayTrigger
                    {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                    key={bridgeType.value}
                    placement="top"
                    overlay={renderMaterialTooltip(
                      bridgeType.label,
                      chartColor,
                    )}
                  >
                    <div
                      className={classNames('material-chip', {
                        active: isChecked,
                        disabled: isViewMode,
                      })}
                      onClick={() => {
                        if (isViewMode || selectedTooth === null) {
                          return
                        }

                        let updatedDentalData = activeDentalChart.dental_data
                        updatedDentalData = updatedDentalData.filter(
                          (data) =>
                            !(
                              String(data.tooth_number) ===
                                selectedTooth.toString() &&
                              (data.condition === 'bridge' ||
                                data.condition === 'abutment')
                            ),
                        )

                        const existingConnector = currentConditions.find(
                          (condition) =>
                            (condition.condition === 'bridge' ||
                              condition.condition === 'abutment') &&
                            condition.type !== null,
                        )
                        const targetCondition =
                          conditionCode === 'abutment' ? 'abutment' : 'bridge'

                        if (isChecked) {
                          // Remove material only; keep the condition and connector state.
                          setBridgeMaterialStage(null)
                          updatedDentalData = addConditionToDentalData(
                            updatedDentalData,
                            selectedTooth,
                            targetCondition,
                            existingConnector?.type ?? null,
                            null,
                            null,
                            false,
                          )
                          commitActiveDentalChartUpdate({
                            ...activeDentalChart,
                            dental_data: updatedDentalData,
                          })
                          dispatch(setDentalUpdated(true))
                          return
                        }

                        const selectedMaterial = bridgeType.value as
                          | 'zirconia'
                          | 'pfm'
                          | 'pfz'
                          | 'metal'
                          | 'ceramic'
                        setBridgeMaterialStage(selectedMaterial)

                        updatedDentalData = addConditionToDentalData(
                          updatedDentalData,
                          selectedTooth,
                          targetCondition,
                          existingConnector?.type ?? null,
                          selectedMaterial,
                          null,
                          false,
                        )
                        commitActiveDentalChartUpdate({
                          ...activeDentalChart,
                          dental_data: updatedDentalData,
                        })
                        dispatch(setDentalUpdated(true))
                      }}
                      style={{
                        backgroundColor: isChecked
                          ? 'var(--blue-200)'
                          : 'transparent',
                        borderColor: 'var(--blue-200)',
                        borderWidth: '1px',
                        borderStyle: 'solid',
                        color: 'var(--blue-600)',
                        borderRadius: '8px',
                      }}
                    >
                      {bridgeType.label}
                    </div>
                  </OverlayTrigger>
                )
              })}
            </div>
          </div>,
        )
      }
    }

    if (conditionCode === 'impacted') {
      sections.push(
        <div key="impacted-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Type</label>
          <ButtonGroup className="d-flex flex-wrap" style={{ gap: '4px' }}>
            {(['left', 'right', 'up', 'down'] as const).map((direction) => {
              const directionCondition = toPayload({
                condition: 'impacted',
                type: direction,
              })
              const isChecked = hasConditionType('impacted', direction)

              return (
                <Button
                  key={direction}
                  size="sm"
                  aria-pressed={isChecked}
                  disabled={isViewMode}
                  className={chartingSidepanelToggleClass(isChecked)}
                  onClick={() => {
                    if (isViewMode) return
                    if (isChecked && selectedTooth !== null) {
                      const updatedDentalData = removeAllConditionRowsForTooth(
                        activeDentalChart.dental_data,
                        selectedTooth,
                        'impacted',
                      )
                      dispatch(setDentalUpdated(true))
                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                      return
                    }
                    onApplyConditionPayload(directionCondition)
                  }}
                >
                  {capitalizeFirstLetter(direction)}
                </Button>
              )
            })}
          </ButtonGroup>
        </div>,
      )
    }

    if (conditionCode === 'drifted') {
      // Determine if upper (palatal) or lower (lingual) based on tooth position
      const isUpperTooth =
        selectedTooth !== null && selectedTooth >= 1 && selectedTooth <= 16
      const palatalLingualValue = isUpperTooth ? 'p' : 'l'
      const palatalLingualLabel = isUpperTooth ? 'Palatal' : 'Lingual'

      const driftedOptions = [
        { label: 'Facial', value: 'b' },
        { label: 'Distal', value: 'd' },
        { label: 'Mesial', value: 'm' },
        { label: palatalLingualLabel, value: palatalLingualValue },
      ]

      sections.push(
        <div key="drifted-type" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Type</label>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '2px',
            }}
          >
            {driftedOptions.map(({ label: driftedLabel, value }) => {
              const isChecked = (conditionEntriesByCode.drifted ?? []).some(
                (condition) => condition.surface?.toLowerCase() === value,
              )

              return (
                <Button
                  key={value}
                  size="sm"
                  aria-pressed={isChecked}
                  className={chartingSidepanelToggleClass(isChecked)}
                  onClick={() => {
                    if (isChecked) {
                      // Remove this specific drifted condition
                      const updatedDentalData = removeConditionFromDentalData(
                        activeDentalChart.dental_data,
                        selectedTooth!,
                        'drifted',
                        null,
                        null,
                        value,
                      )
                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                    } else {
                      // Remove any existing drifted condition first (single selection)
                      let updatedDentalData =
                        activeDentalChart.dental_data.filter((data) => {
                          const dataToothNumber =
                            typeof data.tooth_number === 'string'
                              ? data.tooth_number
                              : String(data.tooth_number)
                          return !(
                            dataToothNumber === selectedTooth!.toString() &&
                            data.condition === 'drifted'
                          )
                        })

                      // Add the new drifted condition
                      updatedDentalData = addConditionToDentalData(
                        updatedDentalData,
                        selectedTooth!,
                        'drifted',
                        null,
                        null,
                        value,
                        false,
                      )

                      commitActiveDentalChartUpdate({
                        ...activeDentalChart,
                        dental_data: updatedDentalData,
                      })
                    }
                  }}
                >
                  {driftedLabel}
                </Button>
              )
            })}
          </div>
        </div>,
      )
    }

    // Render Material selections
    if (conditionCode === 'crown') {
      sections.push(
        <div key="crown-material" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Material</label>
          <div className="d-flex gap-2 flex-wrap">
            {[
              { value: 'zirconia', label: 'Zirconia' },
              { value: 'pfm', label: 'PFM' },
              { value: 'pfz', label: 'PFZ' },
              { value: 'metal', label: 'Metal' },
              { value: 'ceramic', label: 'Ceramic' },
            ].map((crownType) => {
              const crownCondition = toPayload({
                condition: 'crown',
                material: crownType.value,
              })
              const isChecked = hasConditionMaterial('crown', crownType.value)
              const chartColor = getChartMaterialColor(crownType.value, 'crown')

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={crownType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(crownType.label, chartColor)}
                >
                  <div
                    className={classNames('material-chip', {
                      active: isChecked,
                      disabled: isViewMode,
                    })}
                    onClick={() => {
                      if (isViewMode) return
                      if (isChecked && selectedTooth !== null) {
                        const updatedDentalData =
                          removeAllConditionRowsForTooth(
                            activeDentalChart.dental_data,
                            selectedTooth,
                            'crown',
                          )
                        dispatch(setDentalUpdated(true))
                        commitActiveDentalChartUpdate({
                          ...activeDentalChart,
                          dental_data: updatedDentalData,
                        })
                        return
                      }
                      onApplyConditionPayload(crownCondition)
                    }}
                    style={{
                      backgroundColor: isChecked
                        ? 'var(--blue-200)'
                        : 'transparent',
                      borderColor: 'var(--blue-200)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      color: 'var(--blue-600)',
                      borderRadius: '8px',
                    }}
                  >
                    {crownType.label}
                  </div>
                </OverlayTrigger>
              )
            })}
          </div>
        </div>,
      )
    }

    if (conditionCode === 'filling') {
      const persistedFillingMaterialLower = currentConditions
        .find(
          (c) =>
            c.condition === 'filling' &&
            c.material != null &&
            String(c.material).trim() !== '',
        )
        ?.material?.toLowerCase()

      sections.push(
        <div key="filling-material" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Material</label>
          <div className="d-flex gap-2 flex-wrap">
            {[
              { value: 'ceramic', label: 'Ceramic' },
              { value: 'gold', label: 'Gold' },
              { value: 'amalgam', label: 'Amalgam' },
              { value: 'composite', label: 'Composite' },
              { value: 'temporary', label: 'Temporary' },
            ].map((materialType) => {
              const materialCondition = toPayload({
                condition: 'filling',
                material: materialType.value,
              })
              const isChecked = persistedFillingMaterialLower
                ? persistedFillingMaterialLower ===
                  materialType.value.toLowerCase()
                : fillingMaterialDraft === materialType.value
              const chartColor = getChartMaterialColor(
                materialType.value,
                'filling',
              )

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={materialType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(
                    materialType.label,
                    chartColor,
                  )}
                >
                  <div
                    className={classNames('material-chip', {
                      active: isChecked,
                      disabled: isViewMode,
                    })}
                    onClick={() => {
                      if (isViewMode) return
                      if (isChecked) {
                        return
                      }
                      // No filling rows persisted yet → only draft (no dental_data write until a surface).
                      if (!persistedFillingMaterialLower) {
                        const nextDraft = materialType.value as FillingMaterial
                        setFillingMaterialDraft(nextDraft)
                        return
                      }
                      // Let handleApplyCondition manage filling material exclusivity
                      // so existing selected surfaces are preserved on material change.
                      onApplyConditionPayload(materialCondition)
                    }}
                    style={{
                      backgroundColor: isChecked
                        ? 'var(--blue-200)'
                        : 'transparent',
                      borderColor: 'var(--blue-200)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      color: 'var(--blue-600)',
                      borderRadius: '8px',
                    }}
                  >
                    {materialType.label}
                  </div>
                </OverlayTrigger>
              )
            })}
          </div>
        </div>,
      )
    }

    if (conditionCode === 'inlay') {
      sections.push(
        <div key="inlay-material" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Material</label>
          <div className="d-flex gap-2 flex-wrap">
            {[
              { value: 'gold', label: 'Gold' },
              { value: 'zirconia', label: 'Zirconia' },
              { value: 'ceramic', label: 'Ceramic' },
            ].map((materialType) => {
              const materialCondition = toPayload({
                condition: 'inlay',
                material: materialType.value,
              })
              const isChecked = hasConditionMaterial(
                'inlay',
                materialType.value,
              )
              const chartColor = getChartMaterialColor(
                materialType.value,
                'inlay',
              )

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={materialType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(
                    materialType.label,
                    chartColor,
                  )}
                >
                  <div
                    className={classNames('material-chip', {
                      active: isChecked,
                      disabled: isViewMode,
                    })}
                    onClick={() => {
                      if (isViewMode) return
                      if (isChecked) return
                      onApplyConditionPayload(materialCondition)
                    }}
                    style={{
                      backgroundColor: isChecked
                        ? 'var(--blue-200)'
                        : 'transparent',
                      borderColor: 'var(--blue-200)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      color: 'var(--blue-600)',
                      borderRadius: '8px',
                    }}
                  >
                    {materialType.label}
                  </div>
                </OverlayTrigger>
              )
            })}
          </div>
        </div>,
      )
    }

    if (conditionCode === 'onlay') {
      sections.push(
        <div key="onlay-material" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Material</label>
          <div className="d-flex gap-2 flex-wrap">
            {[
              { value: 'gold', label: 'Gold' },
              { value: 'zirconia', label: 'Zirconia' },
              { value: 'ceramic', label: 'Ceramic' },
            ].map((materialType) => {
              const materialCondition = toPayload({
                condition: 'onlay',
                material: materialType.value,
              })
              const isChecked = hasConditionMaterial(
                'onlay',
                materialType.value,
              )
              const chartColor = getChartMaterialColor(
                materialType.value,
                'onlay',
              )

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={materialType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(
                    materialType.label,
                    chartColor,
                  )}
                >
                  <div
                    className={classNames('material-chip', {
                      active: isChecked,
                      disabled: isViewMode,
                    })}
                    onClick={() => {
                      if (isViewMode) return
                      if (isChecked) return
                      onApplyConditionPayload(materialCondition)
                    }}
                    style={{
                      backgroundColor: isChecked
                        ? 'var(--blue-200)'
                        : 'transparent',
                      borderColor: 'var(--blue-200)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      color: 'var(--blue-600)',
                      borderRadius: '8px',
                    }}
                  >
                    {materialType.label}
                  </div>
                </OverlayTrigger>
              )
            })}
          </div>
        </div>,
      )
    }

    if (conditionCode === 'veneer') {
      sections.push(
        <div key="veneer-material" className="mt-2 px-1">
          <label className="form-label mb-1 fw-bold">Material</label>
          <div className="d-flex gap-2 flex-wrap">
            {[
              { value: 'ceramic', label: 'Ceramic' },
              { value: 'composite', label: 'Composite' },
              { value: 'zirconia', label: 'Zirconia' },
            ].map((materialType) => {
              const materialCondition = toPayload({
                condition: 'veneer',
                material: materialType.value,
              })
              const isChecked = hasConditionMaterial(
                'veneer',
                materialType.value,
              )
              const chartColor = getChartMaterialColor(
                materialType.value,
                'veneer',
              )

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={materialType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(
                    materialType.label,
                    chartColor,
                  )}
                >
                  <div
                    className={classNames('material-chip', {
                      active: isChecked,
                      disabled: isViewMode,
                    })}
                    onClick={() => {
                      if (isViewMode) return
                      if (isChecked && selectedTooth !== null) {
                        const updatedDentalData =
                          removeAllConditionRowsForTooth(
                            activeDentalChart.dental_data,
                            selectedTooth,
                            'veneer',
                          )
                        dispatch(setDentalUpdated(true))
                        commitActiveDentalChartUpdate({
                          ...activeDentalChart,
                          dental_data: updatedDentalData,
                        })
                        return
                      }
                      onApplyConditionPayload(materialCondition)
                    }}
                    style={{
                      backgroundColor: isChecked
                        ? 'var(--blue-200)'
                        : 'transparent',
                      borderColor: 'var(--blue-200)',
                      borderWidth: '1px',
                      borderStyle: 'solid',
                      color: 'var(--blue-600)',
                      borderRadius: '8px',
                    }}
                  >
                    {materialType.label}
                  </div>
                </OverlayTrigger>
              )
            })}
          </div>
        </div>,
      )
    }

    // Render Caries Type
    if (conditionCode === 'caries' || conditionCode === 'recurring_caries') {
      const selectedCariesType =
        currentConditions.find(
          (c) =>
            c.condition === conditionCode &&
            c.type !== null &&
            ['d1', 'd2', 'd3', 'e1', 'e2'].includes(c.type.toLowerCase()),
        )?.type ?? 'd1'

      sections.push(
        <div key="caries-type" className="mt-2 px-1">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <label className="form-label mb-0 fw-bold">Type</label>
          </div>
          <ButtonGroup className="d-flex flex-nowrap" style={{ gap: '4px' }}>
            {[
              { value: 'D1', label: 'D1', color: '#FFA7A1' },
              { value: 'D2', label: 'D2', color: '#FE746B' },
              { value: 'D3', label: 'D3', color: '#D8352A' },
              { value: 'E1', label: 'E1', color: '#65ACF5' },
              { value: 'E2', label: 'E2', color: '#185DD8' },
            ].map((cariesType) => {
              const cariesTypeCondition = toPayload({
                condition: conditionCode,
                type: cariesType.value.toLowerCase(),
              })
              const isChecked =
                selectedCariesType.toLowerCase() ===
                cariesType.value.toLowerCase()

              return (
                <OverlayTrigger
                  {...CHARTING_SIDEPANEL_OVERLAY_TRIGGER_OPTS}
                  key={cariesType.value}
                  placement="top"
                  overlay={renderMaterialTooltip(
                    cariesType.label,
                    cariesType.color,
                  )}
                >
                  <Button
                    size="sm"
                    aria-pressed={isChecked}
                    disabled={isViewMode}
                    className={chartingSidepanelToggleClass(isChecked)}
                    onClick={() => {
                      if (isViewMode) {
                        return
                      }

                      if (isChecked) {
                        return
                      }

                      onApplyConditionPayload(cariesTypeCondition)
                    }}
                  >
                    {cariesType.label}
                  </Button>
                </OverlayTrigger>
              )
            })}
          </ButtonGroup>
        </div>,
      )
    }

    // Render Surfaces
    const conditionsWithSurfaces = [...CONDITION_CODES_WITH_SURFACE_GRID]
    if (conditionsWithSurfaces.includes(conditionCode)) {
      const surfaceContent = renderSurfaceSelection(conditionCode)
      if (surfaceContent) {
        sections.push(
          <div key="surfaces" className="mt-2 px-1">
            <label className="form-label mb-1 fw-bold">Surfaces</label>
            {surfaceContent}
          </div>,
        )
      }
    }

    return sections.length > 0 ? <>{sections}</> : null
  }

  const conditionHasRenderableSubItems = (conditionCode: string): boolean => {
    if (!conditionHasSubItems(conditionCode)) {
      return false
    }

    return renderConditionSubItems(conditionCode) !== null
  }

  const handleResetCondition = (conditionCode: string): void => {
    if (isViewMode || selectedTooth === null) {
      return
    }

    const updatedDentalData = removeAllConditionRowsForTooth(
      activeDentalChart.dental_data,
      selectedTooth,
      conditionCode,
    )

    dispatch(setDentalUpdated(true))
    commitActiveDentalChartUpdate({
      ...activeDentalChart,
      dental_data: updatedDentalData,
    })

    if (conditionCode === 'bridge') {
      setBridgeMaterialStage(null)
    }
    if (conditionCode === 'filling') {
      setFillingMaterialDraft('ceramic')
    }
  }

  // Handler function for toggling conditions
  const handleConditionToggle = (conditionCode: string): void => {
    // Prevent editing in view mode
    if (isViewMode) {
      return
    }

    // This will contain all the condition toggle logic from renderConditionButtons
    // For now, we'll call the existing logic
    const isFurcation = conditionCode === 'furcation'
    const isRecession = conditionCode === 'recession'
    const isCrown = conditionCode === 'crown'
    const isVeneer = conditionCode === 'veneer'
    const isInlay = conditionCode === 'inlay'
    const isOnlay = conditionCode === 'onlay'
    const isFracture = conditionCode === 'fracture'
    const isCrack = conditionCode === 'crack'
    const isAttrition = conditionCode === 'attrition'
    const isAbrasion = conditionCode === 'abrasion'

    const isInlayDisabled =
      isInlay &&
      selectedTooth &&
      INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    const isOnlayDisabled =
      isOnlay &&
      selectedTooth &&
      INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    const isRetainerDisabled =
      conditionCode === 'retainer' &&
      selectedTooth &&
      !RETAINER_ALLOWED_TOOTH_IDS.includes(selectedTooth)
    const isSealantsDisabled =
      conditionCode === 'sealants' &&
      selectedTooth &&
      SEALANTS_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    const isFurcationDisabled =
      isFurcation &&
      selectedTooth &&
      !FURCATION_ALLOWED_TOOTH_IDS.includes(selectedTooth)
    const isVeneerDisabled =
      isVeneer &&
      selectedTooth &&
      VENEER_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
    const isDisabled =
      isInlayDisabled ||
      isOnlayDisabled ||
      isRetainerDisabled ||
      isSealantsDisabled ||
      isFurcationDisabled ||
      isVeneerDisabled

    if (isDisabled) {
      if (isInlayDisabled) {
        alert(
          `⚠️ WARNING: Inlays are not applicable to Tooth ${selectedTooth}!\n\nInlays are only applicable to posterior teeth (molars and premolars):\n• Teeth 1-5 (Upper right)\n• Teeth 12-16 (Upper left)\n• Teeth 17-21 (Lower left)\n• Teeth 28-32 (Lower right)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      if (isOnlayDisabled) {
        alert(
          `⚠️ WARNING: Onlays are not applicable to Tooth ${selectedTooth}!\n\nOnlays are only applicable to posterior teeth (molars and premolars):\n• Teeth 1-5 (Upper right)\n• Teeth 12-16 (Upper left)\n• Teeth 17-21 (Lower left)\n• Teeth 28-32 (Lower right)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      if (isRetainerDisabled) {
        alert(
          `⚠️ WARNING: Retainers are not applicable to Tooth ${selectedTooth}!\n\nRetainers are only applicable to:\n• Upper teeth: 4-13 (Premolars and Anterior teeth)\n• Lower teeth: 20-29 (Premolars and Anterior teeth)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      if (isSealantsDisabled) {
        alert(
          `⚠️ WARNING: Sealants are not applicable to Tooth ${selectedTooth}!\n\nSealants are only applicable to posterior teeth (molars and premolars):\n• Teeth 1-5 (Upper right)\n• Teeth 12-16 (Upper left)\n• Teeth 17-21 (Lower left)\n• Teeth 28-32 (Lower right)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      if (isFurcationDisabled) {
        alert(
          `⚠️ WARNING: Furcation is not applicable to Tooth ${selectedTooth}!\n\nFurcation is only applicable to molars and certain premolars:\n• Teeth 1-3 (Upper right molars)\n• Teeth 14-16 (Upper left molars)\n• Teeth 17-19 (Lower left molars)\n• Teeth 30-32 (Lower right molars)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      if (isVeneerDisabled) {
        alert(
          `⚠️ WARNING: Veneer is not applicable to Tooth ${selectedTooth}!\n\nVeneer is only applicable to anterior teeth and premolars:\n• Teeth 4-13 (Upper premolars and anterior teeth)\n• Teeth 20-29 (Lower premolars and anterior teeth)\n\nPlease select a different tooth or treatment option.`,
        )
      }
      return
    }

    const removeViaSurfaceOrResetOnlyConditions = new Set<string>(
      CONDITION_CODES_WITH_SUB_ITEMS,
    )
    const isConditionActive = hasConditionCode(conditionCode)
    if (
      isConditionActive &&
      removeViaSurfaceOrResetOnlyConditions.has(conditionCode)
    ) {
      return
    }

    // Reuse all the existing condition toggle logic from renderConditionButtons
    // This is a large block, so we'll keep it inline for now
    if (isFurcation) {
      // Furcation is applied via type selection only.
      return
    } else if (isRecession) {
      // Recession is applied via class selection only.
      return
    } else if (conditionCode === 'periradicular') {
      // Periradicular is applied via severity selection only.
      return
    } else if (isCrack) {
      // Crack is applied via type selection only.
      return
    } else if (isFracture) {
      // Fracture is applied via surface selection only.
      return
    } else if (isAttrition) {
      // Attrition is applied via severity selection only.
      return
    } else if (isAbrasion) {
      // Abrasion is applied via severity selection only.
      return
    } else if (conditionCode === 'impacted') {
      // Impacted is applied via type (direction) selection only.
      return
    } else if (isCrown) {
      // Crown is applied via material selection only.
      return
    } else if (isVeneer) {
      // Veneer is applied via material selection only.
      return
    } else if (conditionCode === 'bridge') {
      const existing = currentConditions.find((c) => c.condition === 'bridge')

      if (existing) {
        onApplyConditionPayload(
          toPayload({ condition: 'bridge', material: existing.material }),
        )
      } else {
        handleResetCondition('abutment')
        setBridgeMaterialStage('zirconia')
        onApplyConditionPayload(
          toPayload({ condition: 'bridge', material: 'zirconia' }),
        )
      }
    } else if (conditionCode === 'abutment') {
      const existing = currentConditions.find((c) => c.condition === 'abutment')
      if (existing) {
        onApplyConditionPayload(
          toPayload({
            condition: 'abutment',
            type: existing.type,
            material: existing.material,
          }),
        )
      } else {
        handleResetCondition('bridge')
        setBridgeMaterialStage('zirconia')
        onApplyConditionPayload(
          toPayload({
            condition: 'abutment',
            type: 'left_abutment',
            material: 'zirconia',
          }),
        )
      }
    } else if (isInlay) {
      // Inlay is applied via material/surface selection only.
      return
    } else if (isOnlay) {
      // Onlay is applied via material/surface selection only.
      return
    } else if (conditionCode === 'filling') {
      const existing = currentConditions.find((c) => c.condition === 'filling')

      if (existing) {
        // Remove all filling conditions directly from dental_data
        // This preserves all other conditions with their type/material/surface info
        const updatedDentalData = activeDentalChart.dental_data.filter(
          (data) => {
            const dataToothNumber =
              typeof data.tooth_number === 'string'
                ? data.tooth_number
                : String(data.tooth_number)
            // Keep data for other teeth, or for this tooth if it's not filling
            return (
              dataToothNumber !== selectedTooth!.toString() ||
              data.condition !== 'filling'
            )
          },
        )
        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData,
        })
      }
      // Opening filling does not write dental_data (surfaces persist filling; material chips use draft until then).
      return
    } else if (conditionCode === 'erosion') {
      // Erosion is applied via surface selection only.
      return
    } else if (
      conditionCode === 'caries' ||
      conditionCode === 'recurring_caries'
    ) {
      const existing = currentConditions.find(
        (c) => c.condition === conditionCode,
      )

      if (existing) {
        // Remove all rows for this caries/recurring variant on this tooth only
        // This preserves other conditions with their type/material/surface info
        const updatedDentalData2 = activeDentalChart.dental_data.filter(
          (data) => {
            const dataToothNumber =
              typeof data.tooth_number === 'string'
                ? data.tooth_number
                : String(data.tooth_number)
            return (
              dataToothNumber !== selectedTooth!.toString() ||
              data.condition !== conditionCode
            )
          },
        )
        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData2,
        })
      }
      // Do not apply payload when opening: surface selection enables the condition (same as caries)
      return
    } else if (conditionCode === 'missing') {
      const isMissingActive = hasConditionCode('missing')

      if (isMissingActive) {
        // Remove missing condition directly from dental_data
        const updatedDentalData3 = removeConditionFromDentalData(
          activeDentalChart.dental_data,
          selectedTooth!,
          'missing',
          null,
          null,
          null,
        )
        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData3,
        })
      } else {
        // Simply add 'missing' to existing conditions without removing others
        const updatedDentalData4 = addConditionToDentalData(
          activeDentalChart.dental_data,
          selectedTooth!,
          'missing',
          null,
          null,
          null,
          false,
        )
        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData4,
        })
      }
    } else if (conditionCode === 'drifted') {
      const existing = currentConditions.find((c) => c.condition === 'drifted')

      if (existing) {
        // Remove all drifted conditions directly from dental_data
        // This preserves all other conditions with their type/material/surface info
        const updatedDentalData = activeDentalChart.dental_data.filter(
          (data) => {
            const dataToothNumber =
              typeof data.tooth_number === 'string'
                ? data.tooth_number
                : String(data.tooth_number)
            // Keep data for other teeth, or for this tooth if it's not drifted
            return (
              dataToothNumber !== selectedTooth!.toString() ||
              data.condition !== 'drifted'
            )
          },
        )
        commitActiveDentalChartUpdate({
          ...activeDentalChart,
          dental_data: updatedDentalData,
        })
        return
      } else {
        onApplyConditionPayload(toPayload({ condition: 'drifted' }))
      }
    } else {
      onApplyConditionPayload(toPayload({ condition: conditionCode }))
    }
  }

  const cleanupSurfaceRequiredConditionOnClose = (
    conditionCode: string,
    toothNumber: number | null = selectedTooth,
  ): void => {
    if (isViewMode || toothNumber === null) {
      return
    }

    const surfaceRequiredConditions = new Set([
      'caries',
      'recurring_caries',
      'filling',
      'erosion',
      'abrasion',
      'furcation',
      'inlay',
      'onlay',
    ])
    if (!surfaceRequiredConditions.has(conditionCode)) {
      return
    }

    const selectedToothStr = toothNumber.toString()
    const matchingRows = activeDentalChart.dental_data.filter((data) => {
      const dataToothNumber =
        typeof data.tooth_number === 'string'
          ? data.tooth_number
          : String(data.tooth_number)
      const matchesCondition =
        data.condition === conditionCode ||
        data.condition.startsWith(`${conditionCode}_`)
      return dataToothNumber === selectedToothStr && matchesCondition
    })

    const hasAnyEntries = matchingRows.length > 0
    if (!hasAnyEntries) {
      return
    }

    const hasAnySurface = matchingRows.some(
      (row) => row.surface != null && String(row.surface).trim() !== '',
    )
    if (hasAnySurface) {
      return
    }

    const hasInlayOrOnlayMaterial =
      (conditionCode === 'inlay' || conditionCode === 'onlay') &&
      matchingRows.some(
        (row) => row.material != null && String(row.material).trim() !== '',
      )
    if (hasInlayOrOnlayMaterial) {
      return
    }

    const updatedDentalData = activeDentalChart.dental_data.filter((data) => {
      const dataToothNumber =
        typeof data.tooth_number === 'string'
          ? data.tooth_number
          : String(data.tooth_number)
      const matchesCondition =
        data.condition === conditionCode ||
        data.condition.startsWith(`${conditionCode}_`)
      return !(dataToothNumber === selectedToothStr && matchesCondition)
    })

    dispatch(setDentalUpdated(true))
    commitActiveDentalChartUpdate({
      ...activeDentalChart,
      dental_data: updatedDentalData,
    })

    if (conditionCode === 'filling') {
      setFillingMaterialDraft('ceramic')
    }
  }

  useEffect(() => {
    const previousTooth = prevSelectedToothRef.current
    if (isViewMode) {
      prevSelectedToothRef.current = selectedTooth
      return
    }

    if (previousTooth !== null && previousTooth !== selectedTooth) {
      cleanupSurfaceRequiredConditionOnClose('caries', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('recurring_caries', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('filling', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('erosion', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('abrasion', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('furcation', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('inlay', previousTooth)
      cleanupSurfaceRequiredConditionOnClose('onlay', previousTooth)
    }

    prevSelectedToothRef.current = selectedTooth
    // We intentionally key this to tooth changes; cleanup function reads latest chart state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTooth, isViewMode])

  // Function to render condition accordions instead of buttons
  const renderConditionAccordions = (
    labels: string[],
    section: 'conditions' | 'endo' | 'ortho',
  ): React.ReactNode => {
    // Get the appropriate expanded accordion key based on section
    const expandedKey =
      section === 'conditions'
        ? expandedConditionsAccordions
        : expandedOrthodonticsAccordions
    const setExpandedKey =
      section === 'conditions'
        ? setExpandedConditionsAccordions
        : setExpandedOrthodonticsAccordions

    // Filter labels to only show conditions applicable to the selected tooth
    const applicableLabels = labels.filter((label) => {
      const conditionCode = getConditionFromLabel(label)
      return isConditionApplicableToTooth(conditionCode, selectedTooth)
    })

    return (
      <Accordion
        activeKey={expandedKey}
        onSelect={(eventKey) => {
          const key = eventKey as string | null
          if (expandedKey !== null) {
            const closingConditionCode =
              key === expandedKey ? expandedKey : expandedKey
            cleanupSurfaceRequiredConditionOnClose(closingConditionCode)
          }
          // If clicking the same key, close it (set to null)
          // Otherwise, set to the new key
          setExpandedKey(key === expandedKey ? null : key)
        }}
        flush
        className="condition-sub-accordions"
      >
        {applicableLabels.map((label) => {
          const conditionCode = getConditionFromLabel(label)
          const hasSubItems = conditionHasRenderableSubItems(conditionCode)
          const isActive = getConditionActiveState(conditionCode)
          // Check if condition should be disabled
          const isInlayDisabled =
            conditionCode === 'inlay' &&
            selectedTooth &&
            INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isOnlayDisabled =
            conditionCode === 'onlay' &&
            selectedTooth &&
            INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isRetainerDisabled =
            conditionCode === 'retainer' &&
            selectedTooth &&
            !RETAINER_ALLOWED_TOOTH_IDS.includes(selectedTooth)
          const isSealantsDisabled =
            conditionCode === 'sealants' &&
            selectedTooth &&
            SEALANTS_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isFurcationDisabled =
            conditionCode === 'furcation' &&
            selectedTooth &&
            !FURCATION_ALLOWED_TOOTH_IDS.includes(selectedTooth)
          const isVeneerDisabled =
            conditionCode === 'veneer' &&
            selectedTooth &&
            VENEER_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isToothRestrictionDisabled =
            isInlayDisabled ||
            isOnlayDisabled ||
            isRetainerDisabled ||
            isSealantsDisabled ||
            isFurcationDisabled ||
            isVeneerDisabled

          // Disabled includes both tooth restrictions and view mode
          const isDisabled = isToothRestrictionDisabled || isViewMode

          const hasInlayOrOnlayPersistedRow =
            (conditionCode === 'inlay' || conditionCode === 'onlay') &&
            hasConditionCode(conditionCode)

          const showResetButton =
            (isActive || hasInlayOrOnlayPersistedRow) &&
            hasSubItems &&
            !isViewMode &&
            selectedTooth !== null

          return (
            <Accordion.Item
              key={`${section}-${label}`}
              eventKey={conditionCode}
              className={classNames('condition-sub-accordion-item', {
                'has-sub-items': hasSubItems,
                'no-sub-items': !hasSubItems,
              })}
            >
              <Accordion.Header
                className={classNames('condition-sub-accordion-header', {
                  disabled: isDisabled,
                  active: isActive,
                  'has-sub-items': hasSubItems,
                })}
                onClick={(e) => {
                  // In view mode, prevent any action for conditions without sub-items.
                  if (isViewMode) {
                    if (!hasSubItems) {
                      e.preventDefault()
                    }
                    return
                  }

                  // If disabled for tooth restrictions, prevent action
                  if (isToothRestrictionDisabled) {
                    e.preventDefault()
                    return
                  }
                }}
              >
                <div
                  className="d-flex align-items-center w-100 gap-1"
                  onClick={(e): void => {
                    if (isViewMode || isToothRestrictionDisabled) {
                      return
                    }

                    if (!selectedTooth) {
                      e.preventDefault()
                      setShowNoToothModal(true)
                      return
                    }

                    dispatch(setDentalUpdated(true))
                    handleConditionToggle(conditionCode)

                    // Keep no-sub-item conditions from toggling accordion state.
                    if (!hasSubItems) {
                      e.preventDefault()
                      e.stopPropagation()
                    }
                  }}
                >
                  <span className="flex-grow-1">{label}</span>
                  {showResetButton ? (
                    <span
                      role="button"
                      tabIndex={0}
                      title="Reset"
                      className="button button-sm charting-condition-reset-btn flex-shrink-0"
                      aria-label="Reset condition"
                      onClick={(e): void => {
                        e.preventDefault()
                        e.stopPropagation()
                        handleResetCondition(conditionCode)
                      }}
                      onKeyDown={(e): void => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          e.stopPropagation()
                          handleResetCondition(conditionCode)
                        }
                      }}
                    >
                      <i className="bi bi-arrow-counterclockwise" aria-hidden />
                    </span>
                  ) : null}
                </div>
              </Accordion.Header>
              {hasSubItems && (
                <Accordion.Body className="p-0 condition-sub-accordion-body">
                  {renderConditionSubItems(conditionCode)}
                </Accordion.Body>
              )}
            </Accordion.Item>
          )
        })}
      </Accordion>
    )
  }

  const renderChartingInformationBody = (): React.ReactNode => {
    return (
      <>
        <Form.Group className="mb-2">
          <Form.Label className="small">Patient name</Form.Label>
          <Form.Control
            type="text"
            size="sm"
            isInvalid={
              !!validationErrors.patient_id &&
              !selectedPatient &&
              !selectedDentalChart
            }
            placeholder="Patient name"
            value={
              getFullName(selectedDentalChart?.patient ?? selectedPatient) ??
              'No Selected Patient'
            }
            readOnly
          />
          {validationErrors.patient_id && (
            <span className="error-text ps-2">
              {validationErrors.patient_id}
            </span>
          )}
        </Form.Group>
        <Form.Group className="mb-2 position-relative">
          <Form.Label className="small">Provider name</Form.Label>
          {/* Keep ComboBox mounted when switching to view mode — unmounting
              Syncfusion during history select/double-click causes removeChild. */}
          <div className={isPersistedChart ? 'd-none' : undefined}>
            <ComboBoxComponent
              dataSource={providerSearchList}
              allowCustom={true}
              allowFiltering={true}
              filterType="Contains"
              fields={{ text: 'name', value: 'id' }}
              placeholder="Provider name"
              value={
                createDentalChart
                  ? createDentalChart.provider_id
                  : (provId ?? '')
              }
              enabled={!isPersistedChart}
              change={(e) => {
                if (isPersistedChart || e.isInteracted === false) {
                  return
                }
                const selectedValue = e.value
                const previousProviderId = createDentalChart?.provider_id ?? ''
                const nextProviderId =
                  selectedValue === undefined || selectedValue === null
                    ? ''
                    : String(selectedValue)
                if (nextProviderId === previousProviderId) {
                  return
                }
                const isValidProvider = providerSearchList
                  .map((p) => p.id)
                  .includes(selectedValue)
                setProvId(e.value)
                dispatch(
                  setCreateDentalChart({
                    ...createDentalChart,
                    provider_id: e.value,
                  }),
                )
                if (e.value !== undefined) {
                  setValidationErrors((prevErrors) => {
                    const updatedErrors = { ...prevErrors }
                    const hasProviderSelection =
                      selectedValue !== undefined &&
                      selectedValue !== null &&
                      String(selectedValue).trim() !== ''

                    if (!hasProviderSelection) {
                      delete updatedErrors['provider_id']
                    } else if (isValidProvider) {
                      delete updatedErrors['provider_id']
                    } else {
                      updatedErrors['provider_id'] =
                        CHART_PROVIDER_INVALID_ERROR
                    }

                    return updatedErrors
                  })
                  dispatch(setDentalUpdated(true))
                }
              }}
              cssClass={`charting-combobox ${isChartProviderHardError(validationErrors.provider_id) && !isPersistedChart ? ' border-danger' : ''}`}
            />
          </div>
          {isPersistedChart ? (
            <Form.Control
              type="text"
              size="sm"
              placeholder="Provider name"
              value={resolveChartProviderDisplayName(
                activeDentalChart?.provider,
                activeDentalChart?.provider_id,
                providerSearchList,
              )}
              readOnly
            />
          ) : (
            isChartProviderHardError(validationErrors.provider_id) && (
              <span className="error-text ps-2">
                {validationErrors.provider_id}
              </span>
            )
          )}
        </Form.Group>
        <Form.Group className="mb-2">
          <Form.Label className="small">Chart name</Form.Label>
          {/* Keep ComboBox mounted when switching to view mode — unmounting
              Syncfusion during history select/double-click causes removeChild. */}
          <div className={isPersistedChart ? 'd-none' : undefined}>
            <ChartNameComboBox
              value={
                createDentalChart ? createDentalChart.title : (chartName ?? '')
              }
              readOnly={isPersistedChart}
              isInvalid={!!validationErrors.title && isNewDentalChart}
              onChange={(nextTitle) => {
                setChartName(nextTitle)
                dispatch(
                  setCreateDentalChart({
                    ...createDentalChart,
                    title: nextTitle,
                  }),
                )
                if (nextTitle.trim()) {
                  setValidationErrors((prevErrors) => {
                    const updatedErrors = { ...prevErrors }
                    delete updatedErrors['title']
                    return updatedErrors
                  })
                }
                dispatch(setDentalUpdated(true))
              }}
            />
          </div>
          {isPersistedChart ? (
            <Form.Control
              type="text"
              size="sm"
              placeholder="Chart name"
              value={activeDentalChart.title ?? ''}
              readOnly
            />
          ) : (
            validationErrors.title &&
            isNewDentalChart && (
              <span className="error-text ps-2">{validationErrors.title}</span>
            )
          )}
        </Form.Group>
      </>
    )
  }

  const renderXrayFlatConditionList = (
    labels: string[],
    section: 'conditions' | 'endo' | 'ortho',
  ): React.ReactNode => {
    const applicableLabels = labels.filter((label) => {
      const conditionCode = getConditionFromLabel(label)
      return isConditionApplicableToTooth(conditionCode, selectedTooth)
    })

    return (
      <div className="charting-xray-flat-condition-list">
        {applicableLabels.map((label) => {
          const conditionCode = getConditionFromLabel(label)
          const hasSubItems = conditionHasRenderableSubItems(conditionCode)
          const isActive = getConditionActiveState(conditionCode)
          const isInlayDisabled =
            conditionCode === 'inlay' &&
            selectedTooth &&
            INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isOnlayDisabled =
            conditionCode === 'onlay' &&
            selectedTooth &&
            INLAY_ONLAY_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isRetainerDisabled =
            conditionCode === 'retainer' &&
            selectedTooth &&
            !RETAINER_ALLOWED_TOOTH_IDS.includes(selectedTooth)
          const isSealantsDisabled =
            conditionCode === 'sealants' &&
            selectedTooth &&
            SEALANTS_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isFurcationDisabled =
            conditionCode === 'furcation' &&
            selectedTooth &&
            !FURCATION_ALLOWED_TOOTH_IDS.includes(selectedTooth)
          const isVeneerDisabled =
            conditionCode === 'veneer' &&
            selectedTooth &&
            VENEER_RESTRICTED_TOOTH_IDS.includes(selectedTooth)
          const isToothRestrictionDisabled =
            isInlayDisabled ||
            isOnlayDisabled ||
            isRetainerDisabled ||
            isSealantsDisabled ||
            isFurcationDisabled ||
            isVeneerDisabled

          const isDisabled =
            isToothRestrictionDisabled || (isViewMode && !hasSubItems)

          const hasInlayOrOnlayPersistedRow =
            (conditionCode === 'inlay' || conditionCode === 'onlay') &&
            hasConditionCode(conditionCode)

          const showResetButton =
            (isActive || hasInlayOrOnlayPersistedRow) &&
            hasSubItems &&
            !isViewMode &&
            selectedTooth !== null

          const onRowActivate = (): void => {
            if (isToothRestrictionDisabled) {
              return
            }
            if (isViewMode && !hasSubItems) {
              return
            }
            if (hasSubItems && xraySelectedConditionCode === conditionCode) {
              cleanupSurfaceRequiredConditionOnClose(conditionCode)
              setXraySelectedConditionCode(null)
              return
            }
            if (!selectedTooth) {
              setShowNoToothModal(true)
              return
            }
            dispatch(setDentalUpdated(true))
            if (hasSubItems) {
              setXrayShowChartingInfo(false)
              if (
                xraySelectedConditionCode &&
                xraySelectedConditionCode !== conditionCode
              ) {
                cleanupSurfaceRequiredConditionOnClose(
                  xraySelectedConditionCode,
                )
              }
              setXraySelectedConditionCode(conditionCode)
            } else {
              handleConditionToggle(conditionCode)
            }
          }

          return (
            <div
              key={`${section}-${label}`}
              className={classNames(
                'charting-xray-condition-row d-flex align-items-center gap-1 px-2 py-1',
                {
                  'charting-xray-condition-row-active':
                    hasSubItems && xraySelectedConditionCode === conditionCode,
                  'charting-xray-condition-row-disabled': isDisabled,
                  'charting-xray-condition-row-active-state': isActive,
                },
              )}
            >
              <button
                type="button"
                className={classNames(
                  'charting-xray-condition-row-btn btn btn-link flex-grow-1 text-start text-decoration-none',
                  {
                    'charting-xray-condition-row-btn-active':
                      (hasSubItems &&
                        xraySelectedConditionCode === conditionCode) ||
                      (!hasSubItems && isActive),
                  },
                )}
                disabled={isDisabled}
                onClick={onRowActivate}
              >
                <span className="me-1">{label}</span>
              </button>
              {showResetButton ? (
                <button
                  type="button"
                  className="unstyled-button button button-sm charting-condition-reset-btn flex-shrink-0"
                  title="Reset"
                  aria-label="Reset condition"
                  onClick={(e): void => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleResetCondition(conditionCode)
                  }}
                >
                  <i className="bi bi-arrow-counterclockwise" aria-hidden />
                </button>
              ) : null}
              {hasSubItems ? (
                <i
                  className={classNames('charting-xray-condition-chevron', {
                    'charting-xray-condition-chevron-active':
                      xraySelectedConditionCode === conditionCode,
                  })}
                  aria-hidden
                />
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  const renderXrayMasterDetailLayout = (): React.ReactNode => {
    const handleXrayOuterAccordionSelect = (eventKey: string | null): void => {
      setPreferChartingInfo(false)
      const newKey = eventKey ?? ''
      if (
        xraySelectedConditionCode !== null &&
        newKey !== '' &&
        newKey !== xrayOuterAccordionKey
      ) {
        cleanupSurfaceRequiredConditionOnClose(xraySelectedConditionCode)
        setXraySelectedConditionCode(null)
      }
      setXrayOuterAccordionKey(newKey)
      if (newKey === '1') {
        setExpandedConditionsAccordions(null)
      }
      if (newKey === '3') {
        setExpandedOrthodonticsAccordions(null)
      }
    }

    return (
      <div className="charting-panel-container charting-xray-master-detail d-flex flex-column h-100">
        <div className="charting-xray-split d-flex flex-grow-1 overflow-hidden">
          <div className="charting-xray-nav border-end overflow-auto">
            <button
              type="button"
              className={classNames(
                'charting-xray-charting-info-row accordion-button collapsed w-100 text-start border-0 rounded-0 mb-2',
                {
                  active: xrayShowChartingInfo || preferChartingInfo,
                  'invalid border-danger': hasChartingValidationErrors(),
                },
              )}
              onClick={() => {
                setPreferChartingInfo(false)
                if (xrayShowChartingInfo) {
                  setXrayShowChartingInfo(false)
                  setXraySelectedConditionCode(null)
                  return
                }

                setXrayShowChartingInfo(true)
                setXraySelectedConditionCode(null)
              }}
            >
              Charting information
              <i className="float-end" aria-hidden />
            </button>
            <Accordion
              activeKey={resolvedXrayOuterKey}
              onSelect={(eventKey) => {
                handleXrayOuterAccordionSelect(eventKey as string | null)
              }}
              flush
            >
              <Accordion.Item eventKey="1">
                <Accordion.Header>Conditions</Accordion.Header>
                <Accordion.Body className="p-0">
                  {renderXrayFlatConditionList(CONDITIONS, 'conditions')}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="2">
                <Accordion.Header>Endodontics</Accordion.Header>
                <Accordion.Body className="p-0">
                  {renderXrayFlatConditionList(ENDODONTICS, 'endo')}
                </Accordion.Body>
              </Accordion.Item>

              <Accordion.Item eventKey="3">
                <Accordion.Header>Orthodontics</Accordion.Header>
                <Accordion.Body className="p-0">
                  {renderXrayFlatConditionList(ORTHODONTICS, 'ortho')}
                </Accordion.Body>
              </Accordion.Item>

              {voiceStatus ? (
                <Accordion.Item eventKey="4" data-voice-assistant="true">
                  <Accordion.Header>Voice assistant</Accordion.Header>
                  <Accordion.Body className="p-2 dental-voice-accordion-body">
                    {voiceStatus}
                  </Accordion.Body>
                </Accordion.Item>
              ) : null}
            </Accordion>
          </div>
          <div className="charting-xray-detail overflow-auto p-2">
            {xrayShowChartingInfo || preferChartingInfo ? (
              <div className="charting-xray-detail-inner">
                {renderChartingInformationBody()}
              </div>
            ) : null}
            {!xrayShowChartingInfo &&
            !preferChartingInfo &&
            xraySelectedConditionCode ? (
              <div className="charting-xray-detail-inner">
                {renderConditionSubItems(xraySelectedConditionCode)}
              </div>
            ) : null}
            {!xrayShowChartingInfo &&
            !preferChartingInfo &&
            !xraySelectedConditionCode ? (
              <div className="charting-xray-detail-placeholder text-muted small">
                Select charting information or a condition to edit details.
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  // Note: handleConditionToggle is now defined before renderConditionAccordions

  if (sidePanelLayout === 'xray-split') {
    return (
      <>
        {renderXrayMasterDetailLayout()}
        <ChartingSelectConditionFirstModal
          show={showConditionWarning}
          onHide={() => setShowConditionWarning(false)}
        />

        <ChartingSelectToothFirstModal
          show={showNoToothModal}
          onHide={() => setShowNoToothModal(false)}
        />
      </>
    )
  }

  return (
    <>
      <div className="charting-panel-container d-flex flex-column h-100">
        <div className="accordion-scroll flex-grow-11 overflow-auto">
          <Accordion
            activeKey={resolvedAccordionKey}
            onSelect={(eventKey) => {
              setPreferChartingInfo(false)
              const newKey = eventKey as string
              setActiveAccordionKey(newKey)

              // When Conditions accordion opens, collapse all sub-accordions
              if (newKey === '1') {
                setExpandedConditionsAccordions(null)
              }
              // When Endodontics accordion opens, collapse all sub-accordions
              if (newKey === '2') {
                // Endodontics doesn't have sub-accordions, but keep for consistency
              }
              // When Orthodontics accordion opens, collapse all sub-accordions
              if (newKey === '3') {
                setExpandedOrthodonticsAccordions(null)
              }
            }}
            flush
          >
            <Accordion.Item eventKey="0" data-charting-info="true">
              <Accordion.Header
                className={
                  hasChartingValidationErrors() ? 'invalid border-danger' : ''
                }
              >
                Charting information
              </Accordion.Header>
              <Accordion.Body className="p-0">
                {renderChartingInformationBody()}
              </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
              <Accordion.Header>Conditions</Accordion.Header>
              <Accordion.Body className="p-0">
                {renderConditionAccordions(CONDITIONS, 'conditions')}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="2">
              <Accordion.Header>Endodontics</Accordion.Header>
              <Accordion.Body className="p-0">
                {renderConditionAccordions(ENDODONTICS, 'endo')}
              </Accordion.Body>
            </Accordion.Item>

            <Accordion.Item eventKey="3">
              <Accordion.Header>Orthodontics</Accordion.Header>
              <Accordion.Body className="p-0">
                {renderConditionAccordions(ORTHODONTICS, 'ortho')}
              </Accordion.Body>
            </Accordion.Item>

            {voiceStatus ? (
              <Accordion.Item eventKey="4" data-voice-assistant="true">
                <Accordion.Header>Voice assistant</Accordion.Header>
                <Accordion.Body className="p-2 dental-voice-accordion-body">
                  {voiceStatus}
                </Accordion.Body>
              </Accordion.Item>
            ) : null}
          </Accordion>
        </div>
      </div>

      <ChartingSelectConditionFirstModal
        show={showConditionWarning}
        onHide={() => setShowConditionWarning(false)}
      />

      <ChartingSelectToothFirstModal
        show={showNoToothModal}
        onHide={() => setShowNoToothModal(false)}
      />
    </>
  )
}

export default ChartingSidePanel
